// src/components/admin/page-sections/ProductCatalogSection.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useCart } from "../../../context/CartContext";
import Lottie from "lottie-react";
import cartAnimation from "../../../assets/cartAnimation.json";
import LazyImage from "../../LazyImage";

const ProductCatalogSection = ({ content }) => {
  const {
    title = "",
    description = "",
    showSearch = true,
    searchPlaceholder = "Search between all categories...",
    productsPerRow = 3,
    showCategorySlider = true,
    categoryItemSize = "medium",
    categoryBorderColor = "#3B82F6",
    selectedCategoryBorderColor = "#F59E0B",
    productCardStyle = "shadow",
    showQuickAction = true,
    quickActionIcon = "customize",
    backgroundColor = "#F9FAFB",
    initialCategorySlug = "",
    categoryApiPath = "/api/categories",
    productApiPath = "/api/products/basic",
    // category display options
    categoryDisplayMode = "slider",
    categoryShape = "circle",
    categoryTextPosition = "below",
    categoryOverlayOpacity = "0.7",
    categoryTextColor = "#ffffff",
    categoryHoverEffect = "zoom",
    showAllCategories = true,
    selectedCategoryIds = [],
    // product display options
    productDisplayFields = {
      showName: true,
      showPrice: true,
      showDescription: false,
      showNote: false,
      showRating: false,
    },
    productInfoPosition = "below",
    productTextAlignment = "left",
    productNameLines = 2,
    productDescriptionLines = 3,
    productImageAspectRatio = "square",
    productClickBehavior = "navigate",
    productPopupSettings = {
      showGallery: true,
      showDescription: true,
      showPrice: true,
      showNote: true,
      showAttributes: true,
      showAddToCart: true,
      showCustomizeButton: true,
      popupSize: "medium",
    },
    priceDisplayType = "regular",
    allowQuantitySelection = false,
    defaultQuantity = 1,
  } = content;

  const [productQuantities, setProductQuantities] = useState({});
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mainSearchTerm, setMainSearchTerm] = useState("");
  const [categorySearchTerms, setCategorySearchTerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const { addToCart } = useCart();
  const sliderRefs = useRef({});

  //for product navigation or popup
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductPopup, setShowProductPopup] = useState(false);

  const selectedCategoryData = categories.find(
    (cat) => cat._id === selectedCategory
  );

  // Parse grid columns value based on number of products per row
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  // Set category item size based on admin choice
  const categoryItemSizes = {
    small: "w-32 h-32",
    medium: "w-48 h-48",
    large: "w-64 h-64",
  };

  // Product card style options
  const cardStyles = {
    shadow: "shadow-lg",
    outline: "border border-gray-200",
    flat: "",
  };

  // Category shape styles
  const categoryShapeStyles = {
    circle: "rounded-full",
    rectangle: "rounded-md aspect-[16/9]",
    square: "rounded-md aspect-square",
    pill: "rounded-full aspect-[3/1]",
  };

  // Category hover effect styles
  const categoryHoverStyles = {
    zoom: "hover:scale-105",
    flip: "hover:rotate-y-180",
    fade: "hover:opacity-70",
    glow: "hover:shadow-glow",
    none: "",
  };

  // Product image aspect ratio styles
  const productImageRatios = {
    square: "aspect-square",
    portrait: "aspect-[3/4]",
    landscape: "aspect-[4/3]",
    original: "aspect-auto",
  };

  // Product info position layout styles
  const productInfoStyles = {
    below: "flex flex-col",
    right: "flex flex-row",
    left: "flex flex-row-reverse",
    overlay: "relative",
  };

  // Function to create URL-friendly slugs
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  // Function to get category URL for linking
  const getCategoryUrl = (category) => {
    return `/cat/${createSlug(category.name)}`;
  };

  // Function to handle product click
  const handleProductClick = (product) => {
    if (productClickBehavior === "popup") {
      setSelectedProduct(product);
      setShowProductPopup(true);
    } else {
      navigateToProduct(product);
    }
  };

  // Function to navigate to product page
  const navigateToProduct = (product) => {
    const match = product.name.match(/^(.*?)(\d.*)?$/);
    let baseName = match[1] ? match[1].trim() : product.name;
    let sizePart = match[2] ? match[2].trim() : "";

    const slugBase = baseName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    const slugSize = sizePart;
    const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

    window.location.href = `/product/${productSlug}`;
  };

  // Function to set category from URL or click
  const navigateToCategory = (category) => {
    const categorySlug = createSlug(category.name);
    window.history.pushState({}, "", `/cat/${categorySlug}`);
    setSelectedCategory(category._id);
  };

  // Function to scroll category sliders
  const scroll = (direction, parentId) => {
    const container = sliderRefs.current[parentId];
    if (container) {
      const scrollAmount = direction === "left" ? -300 : 300;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Handle category search input
  const handleSearchTermChange = (parentId, term) => {
    setCategorySearchTerms((prev) => ({
      ...prev,
      [parentId]: term,
    }));
  };

  // Get categories by parent ID
  const getCategoriesByParent = (parentId) => {
    return categories.filter(
      (cat) =>
        cat.parentCategory &&
        (cat.parentCategory._id === parentId || cat.parentCategory === parentId)
    );
  };

  // Get products for a specific category
  const getProductsByCategory = (categoryId, searchTerm = "") => {
    return products.filter((product) => {
      const productCatId = product.category?._id?.toString
        ? product.category._id.toString()
        : product.category?._id;

      const targetCatId = categoryId?.toString
        ? categoryId.toString()
        : categoryId;

      return (
        productCatId === targetCatId &&
        (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
  };

  // Add this helper function to get pricing tiers for a product
  const getProductTiers = (product) => {
    if (!product.pricingTiers || product.pricingTiers.length === 0) {
      return [];
    }
    return [...product.pricingTiers].sort(
      (a, b) => a.minQuantity - b.minQuantity
    );
  };

  // Update the handleAddToCart function to use the quantity
  const handleAddToCart = async (product) => {
    try {
      // const token = localStorage.getItem('token');
      // if (!token) {
      //   window.location.href = '/login';
      //   return;
      // }

      // Use the selected quantity or default
      const quantity = productQuantities[product._id] || defaultQuantity || 1;

      await addToCart({
        product,
        quantity: quantity,
        customization: {
          preview: product.images[0]?.data || "",
          description: "",
        },
      });

      // Show notification and scroll to top
      setShowCartNotification(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setShowCartNotification(false), 2000);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  // Load categories and products data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch all data first
        const [categoriesRes, productsRes] = await Promise.all([
          fetch(categoryApiPath).then((res) => res.json()),
          fetch(productApiPath).then((res) => res.json()),
        ]);

        // Fetch parent categories separately for better error handling
        let parentCategoriesData = [];
        try {
          const parentCatRes = await fetch("/api/parent-categories");
          if (parentCatRes.ok) {
            parentCategoriesData = await parentCatRes.json();
          }
        } catch (error) {
          console.error("Error fetching parent categories:", error);
        }

        // 2. Always set all categories first as a default
        let categoriesToUse = categoriesRes;
        let parentsToUse = parentCategoriesData;

        // 3. Only filter if specified (showAllCategories is false AND we have selectedCategoryIds)
        if (
          showAllCategories === false &&
          selectedCategoryIds &&
          selectedCategoryIds.length > 0
        ) {
          // Filter categories based on selection
          const selectedIdsSet = new Set(selectedCategoryIds);
          categoriesToUse = categoriesRes.filter((category) =>
            selectedIdsSet.has(category._id)
          );

          // Create a map of categories to parent for faster lookup
          const categoryParentMap = new Map();
          categoriesRes.forEach((category) => {
            if (category.parentCategory) {
              const parentId =
                typeof category.parentCategory === "object"
                  ? category.parentCategory._id
                  : category.parentCategory;
              categoryParentMap.set(category._id, parentId);
            }
          });

          // Find relevant parent IDs based on selected categories
          const relevantParentIds = new Set();
          selectedCategoryIds.forEach((catId) => {
            const parentId = categoryParentMap.get(catId);
            if (parentId) relevantParentIds.add(parentId);
          });

          // Filter parent categories to only include those with selected children
          parentsToUse = parentCategoriesData.filter((parent) =>
            relevantParentIds.has(parent._id)
          );
        }

        // 4. ALWAYS Initialize search terms for ALL parent categories that will be shown
        const initialSearchTerms = {};
        parentsToUse.forEach((parent) => {
          initialSearchTerms[parent._id] = "";
        });

        // 5. Update all state values
        setCategories(categoriesToUse);
        setProducts(productsRes);
        setParentCategories(parentsToUse);
        setCategorySearchTerms(initialSearchTerms);

        // 6. Handle initial category selection from URL or props
        const pathname = window.location.pathname;
        const urlCategorySlug = pathname.startsWith("/cat/")
          ? pathname.split("/cat/")[1]
          : "";
        const categorySlug = initialCategorySlug || urlCategorySlug;

        if (categorySlug) {
          const matchingCategory = categoriesToUse.find(
            (cat) => createSlug(cat.name) === categorySlug
          );

          if (matchingCategory) {
            setSelectedCategory(matchingCategory._id);
          }
        }

        // 7. Load images for all categories and products that will be shown
        const categoriesToShowIds = new Set(categoriesToUse.map((c) => c._id));

        const loadImages = async () => {
          // Load category images
          const categoryImagePromises = categoriesToUse.map((category) =>
            fetch(`/api/categories/${category._id}/image`)
              .then((res) => res.json())
              .then((imageData) => {
                setCategories((prev) =>
                  prev.map((c) =>
                    c._id === category._id ? { ...c, image: imageData } : c
                  )
                );
              })
              .catch((error) =>
                console.error(
                  `Error loading image for category ${category._id}:`,
                  error
                )
              )
          );

          // Load product images - only for products in categories we're showing
          const productImagePromises = productsRes
            .filter((product) => {
              const productCatId = product.category?._id?.toString
                ? product.category._id.toString()
                : product.category?._id;
              return categoriesToShowIds.has(productCatId);
            })
            .map((product) =>
              fetch(`/api/products/${product._id}/image`)
                .then((res) => res.json())
                .then((imageData) => {
                  setProducts((prev) =>
                    prev.map((p) =>
                      p._id === product._id ? { ...p, images: [imageData] } : p
                    )
                  );
                })
                .catch((error) =>
                  console.error(
                    `Error loading image for product ${product._id}:`,
                    error
                  )
                )
            );

          // Load images in batches to prevent overwhelming the browser
          const batchSize = 10;
          const allPromises = [
            ...categoryImagePromises,
            ...productImagePromises,
          ];

          setProducts((prev) =>
            prev.map((product) => ({
              ...product,
              images: product.images || [],
            }))
          );

          for (let i = 0; i < allPromises.length; i += batchSize) {
            await Promise.allSettled(allPromises.slice(i, i + batchSize));
          }
        };

        // Set loading to false before loading images in the background
        setLoading(false);
        loadImages();
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [
    categoryApiPath,
    productApiPath,
    initialCategorySlug,
    showAllCategories,
    selectedCategoryIds,
  ]);

  // useEffect to initialize quantities based on price type
  useEffect(() => {
    // Initialize with default quantities when products load
    if (products.length > 0) {
      const initialQuantities = {};
      products.forEach((product) => {
        let defaultQty = defaultQuantity;
        if (priceDisplayType === "wholesale" && !defaultQty) {
          defaultQty = 50;
        } else if (priceDisplayType === "regular" && !defaultQty) {
          defaultQty = 1;
        }
        initialQuantities[product._id] = defaultQty;
      });
      setProductQuantities(initialQuantities);
    }
  }, [products, defaultQuantity, priceDisplayType]);

  return (
    <div
      className="container mx-auto px-4 pt-24 mb-32 min-h-screen"
      style={{ backgroundColor }}
    >
      {/* Loading indicator */}
      {loading && (
        // <div className="text-center py-12">
        //   <p className="text-xl text-gray-600">Loading products...</p>
        // </div>
        //<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        <div className="fixed inset-0 flex flex-col items-center justify-center">
          <Lottie
            animationData={require("../../../assets/loading-product.json")}
            loop={true}
            className="h-64 w-64"
          />
        </div>
      )}

      {/* Show message when no category is selected */}
      {/* {!selectedCategory && !loading && (
        <div className="text-center py-12">
          <Lottie animationData={require('../../../assets/loading-product.json')} loop={true} className="h-64 w-64" />
        </div>
      )} */}

      {/* Cart Notification */}
      {showCartNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
            <Lottie
              animationData={cartAnimation}
              loop={false}
              className="h-24 w-24"
            />
            <p className="text-xl">Item added to cart successfully!</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Section Header */}
        {(title || description) && (
          <div className="mb-8 text-center">
            {title && (
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
            )}
            {description && (
              <p className="text-lg text-gray-600">{description}</p>
            )}
          </div>
        )}

        {/* Main Search Bar */}
        {showSearch && (
          <div className="mb-12">
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={mainSearchTerm}
                  onChange={(e) => setMainSearchTerm(e.target.value)}
                  className="w-full px-4 py-4 pl-12 rounded-xl border-2 border-blue-500 focus:outline-none focus:border-blue-600 text-lg"
                />
                <Search
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={24}
                />
              </div>
            </div>
          </div>
        )}

        {/* Parent Categories Section */}
        {parentCategories.length > 0
          ? // If we have parent categories, map through them
            parentCategories.map((parent) => (
              <div key={parent._id} className="mb-16">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <h2 className="text-3xl font-bold text-gray-900">
                    {parent.name}
                  </h2>
                  {showSearch && (
                    <div className="relative w-full md:w-64">
                      <input
                        type="text"
                        placeholder={`Search ${parent.name.toLowerCase()}...`}
                        value={categorySearchTerms[parent._id] || ""}
                        onChange={(e) =>
                          handleSearchTermChange(parent._id, e.target.value)
                        }
                        className="w-full px-4 py-2 pl-10 rounded-full border-2 border-blue-500 focus:outline-none focus:border-blue-600"
                      />
                      <Search
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={16}
                      />
                    </div>
                  )}
                </div>

                {/* Category display based on mode */}
                {showCategorySlider && (
                  <>
                    {categoryDisplayMode === "slider" && (
                      <div className="relative mb-12">
                        <button
                          style={{ transform: "none" }}
                          onClick={() => scroll("left", parent._id)}
                          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
                        >
                          <ChevronLeft size={32} />
                        </button>

                        <div
                          ref={(el) => (sliderRefs.current[parent._id] = el)}
                          className="flex overflow-x-auto hide-scrollbar gap-12 px-16 py-4"
                          style={{ paddingTop: 15 + "px" }}
                        >
                          {getCategoriesByParent(parent._id).map((category) => (
                            <div
                              key={category._id}
                              onClick={() => navigateToCategory(category)}
                              className={`flex-none cursor-pointer transition-transform ${
                                categoryHoverStyles[categoryHoverEffect]
                              } ${
                                selectedCategory === category._id
                                  ? "scale-105"
                                  : ""
                              }`}
                            >
                              <div
                                className={`${categoryItemSizes[categoryItemSize]} overflow-hidden border-4 mb-4 ${categoryShapeStyles[categoryShape]} relative`}
                                style={{
                                  borderColor:
                                    selectedCategory === category._id
                                      ? content.selectedCategoryBorderColor ||
                                        "#F59E0B"
                                      : content.categoryBorderColor ||
                                        "#3B82F6",
                                }}
                              >
                                {/* <LazyImage
                            src={category.image?.data}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          /> */}
                                <img
                                  src={
                                    category.image?.thumbnail
                                      ? `/api${category.image.thumbnail}`
                                      : category.image?.path
                                      ? `/api${category.image.path}`
                                      : category.image?.data ||
                                        "/placeholder-image.jpg"
                                  }
                                  alt={category.name}
                                  className="w-full h-full object-cover"
                                  loading="eager"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/placeholder-image.jpg";
                                  }}
                                />

                                {categoryTextPosition === "overlay" && (
                                  <div
                                    className="absolute inset-0 flex items-center justify-center"
                                    style={{
                                      backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
                                      color: categoryTextColor,
                                    }}
                                  >
                                    <p className="text-center font-medium text-lg px-2">
                                      {category.name}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {categoryTextPosition === "below" && (
                                <p className="text-center font-medium text-gray-800 text-lg max-w-[180px] mx-auto">
                                  {category.name}
                                </p>
                              )}
                              <div className="text-center mt-2">
                                <a
                                  href={getCategoryUrl(category)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* View Products */}
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={() => scroll("right", parent._id)}
                          style={{ transform: "none" }}
                          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
                        >
                          <ChevronRight size={32} />
                        </button>
                      </div>
                    )}

                    {categoryDisplayMode === "grid" && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
                        {getCategoriesByParent(parent._id).map((category) => (
                          <div
                            key={category._id}
                            onClick={() => navigateToCategory(category)}
                            className={`cursor-pointer transition-transform ${
                              categoryHoverStyles[categoryHoverEffect]
                            } ${
                              selectedCategory === category._id
                                ? "scale-105"
                                : ""
                            }`}
                          >
                            <div
                              className={`w-full aspect-square overflow-hidden border-4 mb-2 ${categoryShapeStyles[categoryShape]} relative`}
                              style={{
                                borderColor:
                                  selectedCategory === category._id
                                    ? content.selectedCategoryBorderColor ||
                                      "#F59E0B"
                                    : content.categoryBorderColor || "#3B82F6",
                              }}
                            >
                              {/* <LazyImage
                                src={category.image?.data}
                                alt={category.name}
                                className="w-full h-full object-cover"
                              /> */}
                              <img
                                src={
                                  category.image?.thumbnail
                                    ? `/api${category.image.thumbnail}`
                                    : category.image?.path
                                    ? `/api${category.image.path}`
                                    : category.image?.data ||
                                      "/placeholder-image.jpg"
                                }
                                alt={category.name}
                                className="w-full h-full object-cover"
                                loading="eager"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                              />
                              {categoryTextPosition === "overlay" && (
                                <div
                                  className="absolute inset-0 flex items-center justify-center"
                                  style={{
                                    backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
                                    color: categoryTextColor,
                                  }}
                                >
                                  <p className="text-center font-medium text-lg px-2">
                                    {category.name}
                                  </p>
                                </div>
                              )}
                            </div>

                            {categoryTextPosition === "below" && (
                              <p className="text-center font-medium text-gray-800">
                                {category.name}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {categoryDisplayMode === "fullWidth" && (
                      <div className="relative mb-12">
                        <button
                          onClick={() => {
                            const categories = getCategoriesByParent(
                              parent._id
                            );
                            if (categories.length === 0) return;

                            if (
                              !selectedCategory ||
                              !categories.some(
                                (c) => c._id === selectedCategory
                              )
                            ) {
                              // If no category is selected or selected category is from another parent,
                              // select the first category from this parent
                              navigateToCategory(categories[0]);
                            } else {
                              // Find current index and navigate to previous
                              const currentIndex = categories.findIndex(
                                (c) => c._id === selectedCategory
                              );
                              const prevIndex =
                                currentIndex > 0
                                  ? currentIndex - 1
                                  : categories.length - 1;
                              navigateToCategory(categories[prevIndex]);
                            }
                          }}
                          style={{ transform: "none" }}
                          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
                        >
                          <ChevronLeft size={32} />
                        </button>

                        {(() => {
                          const categories = getCategoriesByParent(parent._id);

                          // If no category is selected or selected category is from another parent,
                          // show the first category from this parent
                          if (
                            !selectedCategory ||
                            !categories.some((c) => c._id === selectedCategory)
                          ) {
                            const category = categories[0];
                            if (!category) return null;

                            return (
                              <div
                                key={category._id}
                                className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
                                onClick={() => navigateToCategory(category)}
                              >
                                {/* <LazyImage
                                  src={category.image?.data}
                                  alt={category.name}
                                  className="w-full h-full object-cover"
                                /> */}
                                <img
                                  src={
                                    category.image?.thumbnail
                                      ? `/api${category.image.thumbnail}`
                                      : category.image?.path
                                      ? `/api${category.image.path}`
                                      : category.image?.data ||
                                        "/placeholder-image.jpg"
                                  }
                                  alt={category.name}
                                  className="w-full h-full object-cover"
                                  loading="eager"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/placeholder-image.jpg";
                                  }}
                                />
                                {categoryTextPosition === "overlay" && (
                                  <div
                                    className="absolute inset-0 flex items-center justify-center"
                                    style={{
                                      backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
                                      color: categoryTextColor,
                                    }}
                                  >
                                    <h3 className="text-3xl font-bold px-4 text-center">
                                      {category.name}
                                    </h3>
                                  </div>
                                )}

                                {categoryTextPosition === "below" && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
                                    <h3 className="text-xl font-bold text-center">
                                      {category.name}
                                    </h3>
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // Otherwise, show the selected category
                          return getCategoriesByParent(parent._id).map(
                            (category) =>
                              selectedCategory === category._id && (
                                <div
                                  key={category._id}
                                  className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
                                  onClick={() => navigateToCategory(category)}
                                >
                                  {/* <LazyImage
                                    src={category.image?.data}
                                    alt={category.name}
                                    className="w-full h-full object-cover"
                                  /> */}
                                  <img
                                    src={
                                      category.image?.thumbnail
                                        ? `/api${category.image.thumbnail}`
                                        : category.image?.path
                                        ? `/api${category.image.path}`
                                        : category.image?.data ||
                                          "/placeholder-image.jpg"
                                    }
                                    alt={category.name}
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/placeholder-image.jpg";
                                    }}
                                  />

                                  {categoryTextPosition === "overlay" && (
                                    <div
                                      className="absolute inset-0 flex items-center justify-center"
                                      style={{
                                        backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
                                        color: categoryTextColor,
                                      }}
                                    >
                                      <h3 className="text-3xl font-bold px-4 text-center">
                                        {category.name}
                                      </h3>
                                    </div>
                                  )}

                                  {categoryTextPosition === "below" && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
                                      <h3 className="text-xl font-bold text-center">
                                        {category.name}
                                      </h3>
                                    </div>
                                  )}
                                </div>
                              )
                          );
                        })()}

                        <button
                          onClick={() => {
                            const categories = getCategoriesByParent(
                              parent._id
                            );
                            if (categories.length === 0) return;

                            if (
                              !selectedCategory ||
                              !categories.some(
                                (c) => c._id === selectedCategory
                              )
                            ) {
                              // If no category is selected or selected category is from another parent,
                              // select the second category (or first if only one exists)
                              navigateToCategory(
                                categories[Math.min(1, categories.length - 1)]
                              );
                            } else {
                              // Find current index and navigate to next
                              const currentIndex = categories.findIndex(
                                (c) => c._id === selectedCategory
                              );
                              const nextIndex =
                                currentIndex < categories.length - 1
                                  ? currentIndex + 1
                                  : 0;
                              navigateToCategory(categories[nextIndex]);
                            }
                          }}
                          style={{ transform: "none" }}
                          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
                        >
                          <ChevronRight size={32} />
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Products Grid for Selected Category within this parent */}
                {selectedCategory &&
                  getCategoriesByParent(parent._id).some(
                    (cat) => cat._id === selectedCategory
                  ) && (
                    <div
                      className={`grid ${gridCols[productsPerRow]} gap-8 mt-8 mx-8`}
                    >
                      {getProductsByCategory(
                        selectedCategory,
                        categorySearchTerms[parent._id] || ""
                      ).map((product) => (
                        <div
                          key={product._id}
                          className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
                        >
                          {/* Product image */}
                          <div
                            className={`relative cursor-pointer ${
                              productInfoPosition === "below"
                                ? "w-full"
                                : productInfoPosition === "right" ||
                                  productInfoPosition === "left"
                                ? "w-1/2"
                                : "w-full"
                            } ${productImageRatios[productImageAspectRatio]}`}
                            //  onClick={() => navigateToProduct(product)}
                            onClick={() => handleProductClick(product)}
                          >
                            {/* <LazyImage
                              src={product.images?.[0]?.data}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            /> */}
                            <img
                              src={
                                product.images && product.images.length > 0
                                  ? product.images[0].thumbnail
                                    ? `/api${product.images[0].thumbnail}`
                                    : product.images[0].path
                                    ? `/api${product.images[0].path}`
                                    : product.images[0].data ||
                                      "/placeholder-image.jpg"
                                  : "/placeholder-image.jpg"
                              }
                              alt={product.name}
                              className="w-full h-full object-cover"
                              loading="eager"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/placeholder-image.jpg";
                              }}
                            />
                            {/* Overlay product info if that position is selected */}
                            {productInfoPosition === "overlay" && (
                              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-4">
                                {productDisplayFields.showName && (
                                  <h3
                                    className={`font-bold mb-1 text-${productTextAlignment}`}
                                    style={{
                                      WebkitLineClamp:
                                        productNameLines > 0
                                          ? productNameLines
                                          : "none",
                                      display:
                                        productNameLines > 0
                                          ? "-webkit-box"
                                          : "block",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {product.name}
                                  </h3>
                                )}

                                {productDisplayFields.showPrice && (
                                  //  <div className={`text-${productTextAlignment} font-bold text-yellow-400 mb-1`}>
                                  //    ${product.pricingTiers && product.pricingTiers.length > 0 ?
                                  //      product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
                                  //      product.basePrice.toFixed(2)}
                                  //  </div>
                                  <div
                                    className={`flex flex-col ${
                                      productTextAlignment === "center"
                                        ? "items-center"
                                        : productTextAlignment === "right"
                                        ? "items-end"
                                        : "items-start"
                                    }`}
                                  >
                                    <span className="text-lg font-bold text-blue-600">
                                      {priceDisplayType === "tier" &&
                                      product.pricingTiers &&
                                      product.pricingTiers.length > 0
                                        ? `$${product.pricingTiers[
                                            product.pricingTiers.length - 1
                                          ].price.toFixed(2)}`
                                        : priceDisplayType === "wholesale" &&
                                          product.basePrice
                                        ? `$${product.basePrice.toFixed(2)}`
                                        : `$${
                                            product.regularPrice
                                              ? product.regularPrice.toFixed(2)
                                              : product.basePrice.toFixed(2)
                                          }`}
                                    </span>

                                    {priceDisplayType === "tier" &&
                                      product.pricingTiers &&
                                      product.pricingTiers.length > 0 && (
                                        <span className="text-xs text-green-600">
                                          Tier price (
                                          {
                                            product.pricingTiers[
                                              product.pricingTiers.length - 1
                                            ].minQuantity
                                          }
                                          +)
                                        </span>
                                      )}
                                    {priceDisplayType === "wholesale" && (
                                      <span className="text-xs text-blue-600">
                                        Wholesale price
                                      </span>
                                    )}
                                  </div>
                                )}

                                {productDisplayFields.showDescription &&
                                  product.description && (
                                    <p
                                      className={`text-sm text-gray-200 text-${productTextAlignment}`}
                                      style={{
                                        WebkitLineClamp:
                                          productDescriptionLines > 0
                                            ? productDescriptionLines
                                            : "none",
                                        display:
                                          productDescriptionLines > 0
                                            ? "-webkit-box"
                                            : "block",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {product.description}
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>

                          {/* Product info not in overlay position */}
                          {productInfoPosition !== "overlay" && (
                            <div
                              className={`p-4 cursor-pointer ${
                                productInfoPosition === "below"
                                  ? "w-full"
                                  : productInfoPosition === "right" ||
                                    productInfoPosition === "left"
                                  ? "w-1/2 flex flex-col justify-center"
                                  : ""
                              }`}
                              //  onClick={() => navigateToProduct(product)}
                              onClick={() => handleProductClick(product)}
                            >
                              {productDisplayFields.showName && (
                                <h3
                                  className={`font-bold mb-2 text-gray-900 text-${productTextAlignment}`}
                                  style={{
                                    WebkitLineClamp:
                                      productNameLines > 0
                                        ? productNameLines
                                        : "none",
                                    display:
                                      productNameLines > 0
                                        ? "-webkit-box"
                                        : "block",
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {product.name}
                                </h3>
                              )}

                              {productDisplayFields.showDescription &&
                                product.description && (
                                  <p
                                    className={`text-sm text-gray-600 mb-2 text-${productTextAlignment}`}
                                    style={{
                                      WebkitLineClamp:
                                        productDescriptionLines > 0
                                          ? productDescriptionLines
                                          : "none",
                                      display:
                                        productDescriptionLines > 0
                                          ? "-webkit-box"
                                          : "block",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {product.description}
                                  </p>
                                )}

                              {productDisplayFields.showNote &&
                                product.note && (
                                  <p
                                    className={`text-sm italic text-gray-500 mb-2 text-${productTextAlignment}`}
                                  >
                                    {product.note}
                                  </p>
                                )}

                              {/* {productDisplayFields.showRating && (
                         <div className={`flex ${productTextAlignment === 'center' ? 'justify-center' : productTextAlignment === 'right' ? 'justify-end' : ''} mb-2`}>
                           {[1, 2, 3, 4, 5].map((star) => (
                             <Star 
                               key={star}
                               size={16}
                               className={star <= (product.rating || 5) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}
                             />
                           ))}
                         </div>
                       )} */}

                              {productDisplayFields.showPrice && (
                                <div
                                  className={`flex items-center justify-between ${
                                    productTextAlignment === "center"
                                      ? "justify-center"
                                      : productTextAlignment === "right"
                                      ? "justify-end"
                                      : ""
                                  }`}
                                >
                                  {/* <span className="text-lg font-bold text-blue-600">
                             ${product.pricingTiers && product.pricingTiers.length > 0 ?
                               product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
                               product.basePrice.toFixed(2)}
                           </span> */}
                                  <span className="text-lg font-bold text-blue-600">
                                    {priceDisplayType === "tier" &&
                                    product.pricingTiers &&
                                    product.pricingTiers.length > 0
                                      ? `$${product.pricingTiers[
                                          product.pricingTiers.length - 1
                                        ].price.toFixed(2)}`
                                      : priceDisplayType === "wholesale" &&
                                        product.basePrice
                                      ? `$${product.basePrice.toFixed(2)}`
                                      : `$${
                                          product.regularPrice
                                            ? product.regularPrice.toFixed(2)
                                            : product.basePrice.toFixed(2)
                                        }`}
                                  </span>

                                  {priceDisplayType === "tier" &&
                                    product.pricingTiers &&
                                    product.pricingTiers.length > 0 && (
                                      <span className="text-xs text-green-600">
                                        Tier price (
                                        {
                                          product.pricingTiers[
                                            product.pricingTiers.length - 1
                                          ].minQuantity
                                        }
                                        +)
                                      </span>
                                    )}
                                  {priceDisplayType === "wholesale" && (
                                    <span className="text-xs text-blue-600">
                                      Wholesale price
                                    </span>
                                  )}

                                  {showQuickAction && (
                                    <div className="flex gap-3">
                                      <button
                                        style={{
                                          boxShadow: "none",
                                          border: "none",
                                        }}
                                        //  onClick={(e) => {
                                        //    e.stopPropagation();
                                        //    navigateToProduct(product);
                                        //  }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleProductClick(product);
                                          //  navigateToProduct(product);
                                        }}
                                        className="text-gray-600 hover:text-blue-600 transition-colors"
                                        title="Customize"
                                      >
                                        {quickActionIcon === "customize" ? (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-6 w-6"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                            />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {getProductsByCategory(
                        selectedCategory,
                        categorySearchTerms[parent._id] || ""
                      ).length === 0 && (
                        <div className="col-span-full text-center py-12">
                          <p className="text-xl text-gray-600">
                            No products found in this category.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            ))
          : // If no parent categories are found, show a message
            !loading && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">No categories found.</p>
              </div>
            )}

        {/* Product Detail Popup */}
        {showProductPopup && selectedProduct && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
            <div
              className={`bg-white rounded-lg shadow-xl relative max-h-screen overflow-auto ${
                productPopupSettings.popupSize === "small"
                  ? "w-full max-w-md"
                  : productPopupSettings.popupSize === "medium"
                  ? "w-full max-w-2xl"
                  : productPopupSettings.popupSize === "large"
                  ? "w-full max-w-4xl"
                  : "w-full max-w-7xl h-full"
              }`}
            >
              {/* Close button */}
              <button
                onClick={() => setShowProductPopup(false)}
                style={{ boxShadow: "none" }}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="flex flex-col md:flex-row">
                {/* Product images */}
                {productPopupSettings.showGallery !== false && (
                  <div className="md:w-1/2 p-4">
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      {selectedProduct.images &&
                      selectedProduct.images.length > 0 ? (
                        // <LazyImage
                        //   src={selectedProduct.images[0]?.data}
                        //   alt={selectedProduct.name}
                        //   className="w-full h-auto object-contain aspect-square"
                        // />
                        <img
                          src={
                            selectedProduct.images[0]?.thumbnail
                              ? `/api${selectedProduct.images[0].thumbnail}`
                              : selectedProduct.images[0]?.path
                              ? `/api${selectedProduct.images[0].path}`
                              : selectedProduct.images[0]?.data ||
                                "/placeholder-image.jpg"
                          }
                          alt={selectedProduct.name}
                          className="w-full h-auto object-contain aspect-square"
                          loading="eager"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/placeholder-image.jpg";
                          }}
                        />
                      ) : (
                        <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
                          No image available
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Product details */}
                <div
                  className={`${
                    productPopupSettings.showGallery !== false
                      ? "md:w-1/2"
                      : "w-full"
                  } p-6`}
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedProduct.name}
                  </h2>

                  {/* {productPopupSettings.showPrice !== false && (
                    <div className="text-xl font-bold text-blue-600 mb-4">
                      ${selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
                        selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2) :
                        selectedProduct.basePrice.toFixed(2)}
                    </div>
                  )} */}
                  {productDisplayFields.showPrice && (
                    <div
                      className={`flex flex-col ${
                        productTextAlignment === "center"
                          ? "items-center"
                          : productTextAlignment === "right"
                          ? "items-end"
                          : "items-start"
                      }`}
                    >
                      <span className="text-lg font-bold text-blue-600">
                        {priceDisplayType === "tier" &&
                        selectedProduct.pricingTiers &&
                        selectedProduct.pricingTiers.length > 0
                          ? `$${selectedProduct.pricingTiers[
                              selectedProduct.pricingTiers.length - 1
                            ].price.toFixed(2)}`
                          : priceDisplayType === "wholesale" &&
                            selectedProduct.basePrice
                          ? `$${selectedProduct.basePrice.toFixed(2)}`
                          : `$${
                              selectedProduct.regularPrice
                                ? selectedProduct.regularPrice.toFixed(2)
                                : selectedProduct.basePrice.toFixed(2)
                            }`}
                      </span>

                      {priceDisplayType === "tier" &&
                        selectedProduct.pricingTiers &&
                        selectedProduct.pricingTiers.length > 0 && (
                          <span className="text-xs text-green-600">
                            Tier price (
                            {
                              selectedProduct.pricingTiers[
                                selectedProduct.pricingTiers.length - 1
                              ].minQuantity
                            }
                            +)
                          </span>
                        )}
                      {priceDisplayType === "wholesale" && (
                        <span className="text-xs text-blue-600">
                          Wholesale price
                        </span>
                      )}
                    </div>
                  )}

                  {productPopupSettings.showDescription !== false &&
                    selectedProduct.description && (
                      <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-1">
                          Description
                        </h3>
                        <p className="text-gray-600">
                          {selectedProduct.description}
                        </p>
                      </div>
                    )}

                  {productPopupSettings.showNote !== false &&
                    selectedProduct.note && (
                      <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-1">Note</h3>
                        <p className="text-gray-600 italic">
                          {selectedProduct.note}
                        </p>
                      </div>
                    )}

                  {productPopupSettings.showAttributes !== false &&
                    selectedProduct.attributes &&
                    selectedProduct.attributes.length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-medium text-gray-700 mb-2">
                          Specifications
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedProduct.attributes.map((attr, index) => (
                            <div key={index} className="flex flex-col">
                              <span className="text-sm text-gray-500">
                                {attr.name}
                              </span>
                              <span className="font-medium">{attr.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-4 mt-6">
                    {productPopupSettings.showCustomizeButton !== false && (
                      <button
                        onClick={() => navigateToProduct(selectedProduct)}
                        className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
                      >
                        Customize
                      </button>
                    )}

                    {productPopupSettings.showAddToCart !== false && (
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentQty =
                                productQuantities[selectedProduct._id] ||
                                defaultQuantity;
                              if (currentQty > 1) {
                                setProductQuantities({
                                  ...productQuantities,
                                  [selectedProduct._id]: currentQty - 1,
                                });
                              }
                            }}
                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={
                              productQuantities[selectedProduct._id] ||
                              defaultQuantity
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              const newQty = parseInt(e.target.value, 10) || 1;
                              setProductQuantities({
                                ...productQuantities,
                                [selectedProduct._id]: newQty,
                              });
                            }}
                            className="w-12 text-center border-x border-gray-200 py-1"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentQty =
                                productQuantities[selectedProduct._id] ||
                                defaultQuantity;
                              setProductQuantities({
                                ...productQuantities,
                                [selectedProduct._id]: currentQty + 1,
                              });
                            }}
                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                          >
                            +
                          </button>
                          <button
                            onClick={() => {
                              handleAddToCart(selectedProduct);
                              setShowProductPopup(false);
                            }}
                            className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
        
        @keyframes notification-animation {
          0% { opacity: 0; transform: scale(0.8); }
          10% { opacity: 1; transform: scale(1.1); }
          20% { transform: scale(1); }
          80% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        
        .animate-notification {
          animation: notification-animation 2s ease-in-out forwards;
        }
        
        @keyframes rotate-y {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(180deg); }
        }

        .hover\:rotate-y-180:hover {
          transform: rotateY(180deg);
          transition: transform 0.5s;
        }

        .hover\:shadow-glow:hover {
          box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.5);
          transition: box-shadow 0.3s;
        }
        .fixed {
          animation: fadeIn 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        /* Make the popup responsive */
        @media (max-width: 640px) {
          .md\\:flex-row {
            flex-direction: column;
          }
          
          .md\\:w-1/2 {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCatalogSection;

// // in case of slow speed, roll back to this version
// //working before category selection with high speed
// src/components/admin/page-sections/ProductCatalogSection.js
// import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
// import { useCart } from '../../../context/CartContext';
// import Lottie from 'lottie-react';
// import cartAnimation from '../../../assets/cartAnimation.json';
// import LazyImage from '../../LazyImage';

// const ProductCatalogSection = ({ content }) => {
//   const {
//     title = '',
//     description = '',
//     showSearch = true,
//     searchPlaceholder = 'Search between all categories...',
//     productsPerRow = 3,
//     showCategorySlider = true,
//     categoryItemSize = 'medium',
//     categoryBorderColor = '#3B82F6',
//     selectedCategoryBorderColor = '#F59E0B',
//     productCardStyle = 'shadow',
//     showQuickAction = true,
//     quickActionIcon = 'customize',
//     backgroundColor = '#F9FAFB',
//     initialCategorySlug = '',
//     categoryApiPath = '/api/categories',
//     productApiPath = '/api/products/basic',
//     // New category display options
//     categoryDisplayMode = 'slider',
//     categoryShape = 'circle',
//     categoryTextPosition = 'below',
//     categoryOverlayOpacity = '0.7',
//     categoryTextColor = '#ffffff',
//     categoryHoverEffect = 'zoom',
//     // New product display options
//     productDisplayFields = {
//       showName: true,
//       showPrice: true,
//       showDescription: false,
//       showNote: false,
//       showRating: false
//     },
//     productInfoPosition = 'below',
//     productTextAlignment = 'left',
//     productNameLines = 2,
//     productDescriptionLines = 3,
//     productImageAspectRatio = 'square',
//     productClickBehavior = 'navigate',
//     productPopupSettings = {
//       showGallery: true,
//       showDescription: true,
//       showPrice: true,
//       showNote: true,
//       showAttributes: true,
//       showAddToCart: true,
//       showCustomizeButton: true,
//       popupSize: 'medium'
//     },
//     priceDisplayType = 'regular',
//     allowQuantitySelection = false,
//     defaultQuantity = 1,
//   } = content;

//   const [productQuantities, setProductQuantities] = useState({});
//   const [categories, setCategories] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [parentCategories, setParentCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [mainSearchTerm, setMainSearchTerm] = useState('');
//   const [categorySearchTerms, setCategorySearchTerms] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [showCartNotification, setShowCartNotification] = useState(false);
//   const { addToCart } = useCart();
//   const sliderRefs = useRef({});

//   //for product navigation or popup
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [showProductPopup, setShowProductPopup] = useState(false);

//   const selectedCategoryData = categories.find(cat => cat._id === selectedCategory);

//   // Parse grid columns value based on number of products per row
//   const gridCols = {
//     1: 'grid-cols-1',
//     2: 'grid-cols-1 md:grid-cols-2',
//     3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
//     4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
//   };

//   // Set category item size based on admin choice
//   const categoryItemSizes = {
//     small: 'w-32 h-32',
//     medium: 'w-48 h-48',
//     large: 'w-64 h-64'
//   };

//   // Product card style options
//   const cardStyles = {
//     shadow: 'shadow-lg',
//     outline: 'border border-gray-200',
//     flat: ''
//   };

//   // Category shape styles
//   const categoryShapeStyles = {
//     circle: 'rounded-full',
//     rectangle: 'rounded-md aspect-[16/9]',
//     square: 'rounded-md aspect-square',
//     pill: 'rounded-full aspect-[3/1]'
//   };

//   // Category hover effect styles
//   const categoryHoverStyles = {
//     zoom: 'hover:scale-105',
//     flip: 'hover:rotate-y-180',
//     fade: 'hover:opacity-70',
//     glow: 'hover:shadow-glow',
//     none: ''
//   };

//   // Product image aspect ratio styles
//   const productImageRatios = {
//     square: 'aspect-square',
//     portrait: 'aspect-[3/4]',
//     landscape: 'aspect-[4/3]',
//     original: 'aspect-auto'
//   };

//   // Product info position layout styles
//   const productInfoStyles = {
//     below: 'flex flex-col',
//     right: 'flex flex-row',
//     left: 'flex flex-row-reverse',
//     overlay: 'relative'
//   };

//   // Function to create URL-friendly slugs
//   const createSlug = (name) => {
//     return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
//   };

//   // Function to get category URL for linking
//   const getCategoryUrl = (category) => {
//     return `/cat/${createSlug(category.name)}`;
//   };

//   // Function to handle product click
//   const handleProductClick = (product) => {
//     if (productClickBehavior === 'popup') {
//       setSelectedProduct(product);
//       setShowProductPopup(true);
//     } else {
//       navigateToProduct(product);
//     }
//   };

//   // Function to navigate to product page
//   const navigateToProduct = (product) => {
//     const match = product.name.match(/^(.*?)(\d.*)?$/);
//     let baseName = match[1] ? match[1].trim() : product.name;
//     let sizePart = match[2] ? match[2].trim() : '';

//     const slugBase = baseName
//       .toLowerCase()
//       .replace(/\s+/g, '-')
//       .replace(/[^a-z0-9-]/g, '');

//     const slugSize = sizePart;
//     const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

//     window.location.href = `/product/${productSlug}`;
//   };

//   // Function to set category from URL or click
//   const navigateToCategory = (category) => {
//     const categorySlug = createSlug(category.name);
//     window.history.pushState({}, '', `/cat/${categorySlug}`);
//     setSelectedCategory(category._id);
//   };

//   // Function to scroll category sliders
//   const scroll = (direction, parentId) => {
//     const container = sliderRefs.current[parentId];
//     if (container) {
//       const scrollAmount = direction === 'left' ? -300 : 300;
//       container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
//     }
//   };

//   // Handle category search input
//   const handleSearchTermChange = (parentId, term) => {
//     setCategorySearchTerms(prev => ({
//       ...prev,
//       [parentId]: term
//     }));
//   };

//   // Get categories by parent ID
//   const getCategoriesByParent = (parentId) => {
//     return categories.filter(cat =>
//       cat.parentCategory &&
//       (cat.parentCategory._id === parentId || cat.parentCategory === parentId)
//     );
//   };

//   // Get products for a specific category
//   const getProductsByCategory = (categoryId, searchTerm = '') => {
//     return products.filter(product => {
//       const productCatId = product.category?._id?.toString ?
//         product.category._id.toString() :
//         product.category?._id;

//       const targetCatId = categoryId?.toString ? categoryId.toString() : categoryId;

//       return productCatId === targetCatId &&
//         (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//          product.description.toLowerCase().includes(searchTerm.toLowerCase()));
//     });
//   };

//   // Add this helper function to get pricing tiers for a product
//   const getProductTiers = (product) => {
//     if (!product.pricingTiers || product.pricingTiers.length === 0) {
//       return [];
//     }
//     return [...product.pricingTiers].sort((a, b) => a.minQuantity - b.minQuantity);
//   };

//   // Add product to cart
//   // const handleAddToCart = async (product) => {
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     if (!token) {
//   //       window.location.href = '/login';
//   //       return;
//   //     }

//   //     await addToCart({
//   //       product,
//   //       quantity: 1,
//   //       customization: {
//   //         preview: product.images[0]?.data || '',
//   //         description: ''
//   //       }
//   //     });

//   //     // Show notification and scroll to top
//   //     setShowCartNotification(true);
//   //     window.scrollTo({ top: 0, behavior: 'smooth' });
//   //     setTimeout(() => setShowCartNotification(false), 2000);
//   //   } catch (error) {
//   //     console.error('Error adding to cart:', error);
//   //   }
//   // };
//   // Update the handleAddToCart function to use the quantity
//   const handleAddToCart = async (product) => {
//     try {
//       const token = localStorage.getItem('token');
//       if (!token) {
//         window.location.href = '/login';
//         return;
//       }

//       // Use the selected quantity or default
//       const quantity = productQuantities[product._id] || defaultQuantity || 1;

//       await addToCart({
//         product,
//         quantity: quantity,
//         customization: {
//           preview: product.images[0]?.data || '',
//           description: ''
//         }
//       });

//       // Show notification and scroll to top
//       setShowCartNotification(true);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//       setTimeout(() => setShowCartNotification(false), 2000);
//     } catch (error) {
//       console.error('Error adding to cart:', error);
//     }
//   };

//   // Load categories and products data
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         // First fetch basic data without images (fast)
//         const [categoriesRes, productsRes] = await Promise.all([
//           fetch(categoryApiPath).then(res => res.json()),
//           fetch(productApiPath).then(res => res.json())
//         ]);

//         setCategories(categoriesRes);
//         setProducts(productsRes);

//         // Fetch parent categories
//         const parentCatRes = await fetch('/api/parent-categories');
//         if (parentCatRes.ok) {
//           const data = await parentCatRes.json();
//           setParentCategories(data);

//           // Initialize search terms for each parent category
//           const initialSearchTerms = {};
//           data.forEach(parent => {
//             initialSearchTerms[parent._id] = '';
//           });
//           setCategorySearchTerms(initialSearchTerms);
//         }

//         // Check for initial category selection from URL or props
//         const pathname = window.location.pathname;
//         const urlCategorySlug = pathname.startsWith('/cat/') ? pathname.split('/cat/')[1] : '';
//         const categorySlug = initialCategorySlug || urlCategorySlug;

//         if (categorySlug) {
//           const matchingCategory = categoriesRes.find(cat =>
//             createSlug(cat.name) === categorySlug
//           );

//           if (matchingCategory) {
//             setSelectedCategory(matchingCategory._id);
//           }
//         }

//         setLoading(false);

//         // Then fetch images in parallel
//         const categoryImagePromises = categoriesRes.map(category =>
//           fetch(`/api/categories/${category._id}/image`)
//             .then(res => res.json())
//             .then(imageData => {
//               setCategories(prev => prev.map(c =>
//                 c._id === category._id ? { ...c, image: imageData } : c
//               ));
//             })
//             .catch(error => console.error(`Error loading image for category ${category._id}:`, error))
//         );

//         const productImagePromises = productsRes.map(product =>
//           fetch(`/api/products/${product._id}/image`)
//             .then(res => res.json())
//             .then(imageData => {
//               setProducts(prev => prev.map(p =>
//                 p._id === product._id ? { ...p, images: [imageData] } : p
//               ));
//             })
//             .catch(error => console.error(`Error loading image for product ${product._id}:`, error))
//         );

//         // Load all images in parallel
//         await Promise.allSettled([...categoryImagePromises, ...productImagePromises]);
//       } catch (error) {
//         console.error('Error fetching data:', error);
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [categoryApiPath, productApiPath, initialCategorySlug]);

//   // Add this useEffect to initialize quantities based on price type
//   useEffect(() => {
//     // Initialize with default quantities when products load
//     if (products.length > 0) {
//       const initialQuantities = {};
//       products.forEach(product => {
//         let defaultQty = defaultQuantity;
//         if (priceDisplayType === 'wholesale' && !defaultQty) {
//           defaultQty = 50;
//         } else if (priceDisplayType === 'regular' && !defaultQty) {
//           defaultQty = 1;
//         }
//         initialQuantities[product._id] = defaultQty;
//       });
//       setProductQuantities(initialQuantities);
//     }
//   }, [products, defaultQuantity, priceDisplayType]);

//   return (
//     <div className="container mx-auto px-4 pt-24 mb-32 min-h-screen" style={{ backgroundColor }}>

//       {/* Loading indicator */}
//       {loading && (
//         // <div className="text-center py-12">
//         //   <p className="text-xl text-gray-600">Loading products...</p>
//         // </div>
//         //<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
//         <div className="fixed inset-0 flex flex-col items-center justify-center">
//             <Lottie animationData={require('../../../assets/loading-product.json')} loop={true} className="h-64 w-64" />
//         </div>
//       )}

//       {/* Show message when no category is selected */}
//       {/* {!selectedCategory && !loading && (
//         <div className="text-center py-12">
//           <Lottie animationData={require('../../../assets/loading-product.json')} loop={true} className="h-64 w-64" />
//         </div>
//       )} */}

//       {/* Cart Notification */}
//       {showCartNotification && (
//         <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
//           <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
//             <Lottie animationData={cartAnimation} loop={false} className="h-24 w-24" />
//             <p className="text-xl">Item added to cart successfully!</p>
//           </div>
//         </div>
//       )}

//       <div className="max-w-7xl mx-auto px-4 py-8">
//         {/* Section Header */}
//         {(title || description) && (
//           <div className="mb-8 text-center">
//             {title && <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>}
//             {description && <p className="text-lg text-gray-600">{description}</p>}
//           </div>
//         )}

//         {/* Main Search Bar */}
//         {showSearch && (
//           <div className="mb-12">
//             <div className="max-w-3xl mx-auto">
//               <div className="relative">
//                 <input
//                   type="text"
//                   placeholder={searchPlaceholder}
//                   value={mainSearchTerm}
//                   onChange={(e) => setMainSearchTerm(e.target.value)}
//                   className="w-full px-4 py-4 pl-12 rounded-xl border-2 border-blue-500 focus:outline-none focus:border-blue-600 text-lg"
//                 />
//                 <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Parent Categories Section */}
//         {parentCategories.length > 0 ? (
//           // If we have parent categories, map through them
//           parentCategories.map(parent => (
//             <div key={parent._id} className="mb-16">
//               <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
//                 <h2 className="text-3xl font-bold text-gray-900">{parent.name}</h2>
//                 {showSearch && (
//                   <div className="relative w-full md:w-64">
//                     <input
//                       type="text"
//                       placeholder={`Search ${parent.name.toLowerCase()}...`}
//                       value={categorySearchTerms[parent._id] || ''}
//                       onChange={(e) => handleSearchTermChange(parent._id, e.target.value)}
//                       className="w-full px-4 py-2 pl-10 rounded-full border-2 border-blue-500 focus:outline-none focus:border-blue-600"
//                     />
//                     <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
//                   </div>
//                 )}
//               </div>

//               {/* Category display based on mode */}
//               {showCategorySlider && (
//                  <>
//                  {categoryDisplayMode === 'slider' && (
//                   <div className="relative mb-12">
//                   <button
//                     style={{transform: 'none'}}
//                     onClick={() => scroll('left', parent._id)}
//                     className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                   >
//                     <ChevronLeft size={32} />
//                   </button>

//                   <div
//                     ref={el => sliderRefs.current[parent._id] = el}
//                     className="flex overflow-x-auto hide-scrollbar gap-12 px-16 py-4"
//                     style={{paddingTop: 15+'px'}}
//                   >
//                     {getCategoriesByParent(parent._id).map((category) => (
//                       <div
//                         key={category._id}
//                         onClick={() => navigateToCategory(category)}
//                         className={`flex-none cursor-pointer transition-transform ${
//                           categoryHoverStyles[categoryHoverEffect]
//                         } ${selectedCategory === category._id ? 'scale-105' : ''}`}
//                       >
//                         <div
//                           className={`${categoryItemSizes[categoryItemSize]} overflow-hidden border-4 mb-4 ${categoryShapeStyles[categoryShape]} relative`}
//                           style={{
//                             borderColor: selectedCategory === category._id
//                               ? content.selectedCategoryBorderColor || '#F59E0B'
//                               : content.categoryBorderColor || '#3B82F6'
//                           }}
//                         >
//                           <LazyImage
//                             src={category.image?.data}
//                             alt={category.name}
//                             className="w-full h-full object-cover"
//                           />

//                           {categoryTextPosition === 'overlay' && (
//                             <div
//                               className="absolute inset-0 flex items-center justify-center"
//                               style={{
//                                 backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                 color: categoryTextColor
//                               }}
//                             >
//                               <p className="text-center font-medium text-lg px-2">{category.name}</p>
//                             </div>
//                           )}
//                         </div>

//                         {categoryTextPosition === 'below' && (
//                           <p className="text-center font-medium text-gray-800 text-lg max-w-[180px] mx-auto">{category.name}</p>
//                         )}
//                         <div className="text-center mt-2">
//                           <a
//                             href={getCategoryUrl(category)}
//                             className="text-blue-600 hover:text-blue-800 text-sm font-medium"
//                             onClick={(e) => e.stopPropagation()}
//                           >
//                             {/* View Products */}
//                           </a>
//                         </div>
//                       </div>
//                     ))}
//                   </div>

//                   <button
//                     onClick={() => scroll('right', parent._id)}
//                     style={{transform: 'none'}}
//                     className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                   >
//                     <ChevronRight size={32} />
//                   </button>
//                   </div>
//                  )}

//                  {categoryDisplayMode === 'grid' && (
//                   <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
//                   {getCategoriesByParent(parent._id).map((category) => (
//                     <div
//                       key={category._id}
//                       onClick={() => navigateToCategory(category)}
//                       className={`cursor-pointer transition-transform ${
//                         categoryHoverStyles[categoryHoverEffect]
//                       } ${selectedCategory === category._id ? 'scale-105' : ''}`}
//                     >
//                       <div
//                         className={`w-full aspect-square overflow-hidden border-4 mb-2 ${categoryShapeStyles[categoryShape]} relative`}
//                         style={{
//                           borderColor: selectedCategory === category._id
//                             ? content.selectedCategoryBorderColor || '#F59E0B'
//                             : content.categoryBorderColor || '#3B82F6'
//                         }}
//                       >
//                         <LazyImage
//                           src={category.image?.data}
//                           alt={category.name}
//                           className="w-full h-full object-cover"
//                         />

//                         {categoryTextPosition === 'overlay' && (
//                           <div
//                             className="absolute inset-0 flex items-center justify-center"
//                             style={{
//                               backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                               color: categoryTextColor
//                             }}
//                           >
//                             <p className="text-center font-medium text-lg px-2">{category.name}</p>
//                           </div>
//                         )}
//                       </div>

//                       {categoryTextPosition === 'below' && (
//                         <p className="text-center font-medium text-gray-800">{category.name}</p>
//                       )}
//                     </div>
//                   ))}
//                   </div>
//                  )}

//                  {categoryDisplayMode === 'fullWidth' && (
//                     <div className="relative mb-12">
//                     <button
//                       onClick={() => {
//                         const categories = getCategoriesByParent(parent._id);
//                         if (categories.length === 0) return;

//                         if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                           // If no category is selected or selected category is from another parent,
//                           // select the first category from this parent
//                           navigateToCategory(categories[0]);
//                         } else {
//                           // Find current index and navigate to previous
//                           const currentIndex = categories.findIndex(c => c._id === selectedCategory);
//                           const prevIndex = currentIndex > 0 ? currentIndex - 1 : categories.length - 1;
//                           navigateToCategory(categories[prevIndex]);
//                         }
//                       }}
//                       style={{transform: 'none'}}
//                       className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                     >
//                       <ChevronLeft size={32} />
//                     </button>

//                     {(() => {
//                       const categories = getCategoriesByParent(parent._id);

//                       // If no category is selected or selected category is from another parent,
//                       // show the first category from this parent
//                       if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                         const category = categories[0];
//                         if (!category) return null;

//                         return (
//                           <div
//                             key={category._id}
//                             className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
//                             onClick={() => navigateToCategory(category)}
//                           >
//                             <LazyImage
//                               src={category.image?.data}
//                               alt={category.name}
//                               className="w-full h-full object-cover"
//                             />

//                             {categoryTextPosition === 'overlay' && (
//                               <div
//                                 className="absolute inset-0 flex items-center justify-center"
//                                 style={{
//                                   backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                   color: categoryTextColor
//                                 }}
//                               >
//                                 <h3 className="text-3xl font-bold px-4 text-center">{category.name}</h3>
//                               </div>
//                             )}

//                             {categoryTextPosition === 'below' && (
//                               <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
//                                 <h3 className="text-xl font-bold text-center">{category.name}</h3>
//                               </div>
//                             )}
//                           </div>
//                         );
//                       }

//                       // Otherwise, show the selected category
//                       return getCategoriesByParent(parent._id).map((category) => (
//                         selectedCategory === category._id && (
//                           <div
//                             key={category._id}
//                             className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
//                             onClick={() => navigateToCategory(category)}
//                           >
//                             <LazyImage
//                               src={category.image?.data}
//                               alt={category.name}
//                               className="w-full h-full object-cover"
//                             />

//                             {categoryTextPosition === 'overlay' && (
//                               <div
//                                 className="absolute inset-0 flex items-center justify-center"
//                                 style={{
//                                   backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                   color: categoryTextColor
//                                 }}
//                               >
//                                 <h3 className="text-3xl font-bold px-4 text-center">{category.name}</h3>
//                               </div>
//                             )}

//                             {categoryTextPosition === 'below' && (
//                               <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
//                                 <h3 className="text-xl font-bold text-center">{category.name}</h3>
//                               </div>
//                             )}
//                           </div>
//                         )
//                       ));
//                     })()}

//                     <button
//                       onClick={() => {
//                         const categories = getCategoriesByParent(parent._id);
//                         if (categories.length === 0) return;

//                         if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                           // If no category is selected or selected category is from another parent,
//                           // select the second category (or first if only one exists)
//                           navigateToCategory(categories[Math.min(1, categories.length - 1)]);
//                         } else {
//                           // Find current index and navigate to next
//                           const currentIndex = categories.findIndex(c => c._id === selectedCategory);
//                           const nextIndex = currentIndex < categories.length - 1 ? currentIndex + 1 : 0;
//                           navigateToCategory(categories[nextIndex]);
//                         }
//                       }}
//                       style={{transform: 'none'}}
//                       className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                     >
//                       <ChevronRight size={32} />
//                     </button>
//                   </div>
//                  )}
//                </>
//              )}

//               {/* Products Grid for Selected Category within this parent */}
//               {selectedCategory && getCategoriesByParent(parent._id).some(cat => cat._id === selectedCategory) && (
//                 <div className={`grid ${gridCols[productsPerRow]} gap-8 mt-8 mx-8`}>
//                   {getProductsByCategory(selectedCategory, categorySearchTerms[parent._id] || '').map((product) => (
//                    <div
//                    key={product._id}
//                    className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
//                  >
//                    {/* Product image */}
//                    <div
//                      className={`relative cursor-pointer ${
//                        productInfoPosition === 'below' ? 'w-full' :
//                        productInfoPosition === 'right' || productInfoPosition === 'left' ? 'w-1/2' : 'w-full'
//                      } ${productImageRatios[productImageAspectRatio]}`}
//                     //  onClick={() => navigateToProduct(product)}
//                     onClick={() => handleProductClick(product)}

//                    >
//                      <LazyImage
//                        src={product.images?.[0]?.data}
//                        alt={product.name}
//                        className="w-full h-full object-cover"
//                      />

//                      {/* Overlay product info if that position is selected */}
//                      {productInfoPosition === 'overlay' && (
//                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-4">
//                          {productDisplayFields.showName && (
//                            <h3
//                              className={`font-bold mb-1 text-${productTextAlignment}`}
//                              style={{
//                                WebkitLineClamp: productNameLines > 0 ? productNameLines : 'none',
//                                display: productNameLines > 0 ? '-webkit-box' : 'block',
//                                WebkitBoxOrient: 'vertical',
//                                overflow: 'hidden'
//                              }}
//                            >
//                              {product.name}
//                            </h3>
//                          )}

//                          {productDisplayFields.showPrice && (
//                           //  <div className={`text-${productTextAlignment} font-bold text-yellow-400 mb-1`}>
//                           //    ${product.pricingTiers && product.pricingTiers.length > 0 ?
//                           //      product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                           //      product.basePrice.toFixed(2)}
//                           //  </div>
//                           <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
//                             <span className="text-lg font-bold text-blue-600">
//                               {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
//                                 `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
//                               priceDisplayType === 'wholesale' && product.basePrice ?
//                                 `$${product.basePrice.toFixed(2)}` :
//                                 `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
//                             </span>

//                             {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
//                               <span className="text-xs text-green-600">
//                                 Tier price ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+)
//                               </span>
//                             )}
//                             {priceDisplayType === 'wholesale' && (
//                               <span className="text-xs text-blue-600">Wholesale price</span>
//                             )}
//                           </div>
//                          )}

//                          {productDisplayFields.showDescription && product.description && (
//                            <p
//                              className={`text-sm text-gray-200 text-${productTextAlignment}`}
//                              style={{
//                                WebkitLineClamp: productDescriptionLines > 0 ? productDescriptionLines : 'none',
//                                display: productDescriptionLines > 0 ? '-webkit-box' : 'block',
//                                WebkitBoxOrient: 'vertical',
//                                overflow: 'hidden'
//                              }}
//                            >
//                              {product.description}
//                            </p>
//                          )}
//                        </div>
//                      )}
//                    </div>

//                    {/* Product info not in overlay position */}
//                    {productInfoPosition !== 'overlay' && (
//                      <div
//                        className={`p-4 cursor-pointer ${
//                          productInfoPosition === 'below' ? 'w-full' :
//                          productInfoPosition === 'right' || productInfoPosition === 'left' ? 'w-1/2 flex flex-col justify-center' : ''
//                        }`}
//                       //  onClick={() => navigateToProduct(product)}
//                       onClick={() => handleProductClick(product)}

//                      >
//                        {productDisplayFields.showName && (
//                          <h3
//                            className={`font-bold mb-2 text-gray-900 text-${productTextAlignment}`}
//                            style={{
//                              WebkitLineClamp: productNameLines > 0 ? productNameLines : 'none',
//                              display: productNameLines > 0 ? '-webkit-box' : 'block',
//                              WebkitBoxOrient: 'vertical',
//                              overflow: 'hidden'
//                            }}
//                          >
//                            {product.name}
//                          </h3>
//                        )}

//                        {productDisplayFields.showDescription && product.description && (
//                          <p
//                            className={`text-sm text-gray-600 mb-2 text-${productTextAlignment}`}
//                            style={{
//                              WebkitLineClamp: productDescriptionLines > 0 ? productDescriptionLines : 'none',
//                              display: productDescriptionLines > 0 ? '-webkit-box' : 'block',
//                              WebkitBoxOrient: 'vertical',
//                              overflow: 'hidden'
//                            }}
//                          >
//                            {product.description}
//                          </p>
//                        )}

//                        {productDisplayFields.showNote && product.note && (
//                          <p className={`text-sm italic text-gray-500 mb-2 text-${productTextAlignment}`}>
//                            {product.note}
//                          </p>
//                        )}

//                        {/* {productDisplayFields.showRating && (
//                          <div className={`flex ${productTextAlignment === 'center' ? 'justify-center' : productTextAlignment === 'right' ? 'justify-end' : ''} mb-2`}>
//                            {[1, 2, 3, 4, 5].map((star) => (
//                              <Star
//                                key={star}
//                                size={16}
//                                className={star <= (product.rating || 5) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}
//                              />
//                            ))}
//                          </div>
//                        )} */}

//                        {productDisplayFields.showPrice && (
//                          <div className={`flex items-center justify-between ${productTextAlignment === 'center' ? 'justify-center' : productTextAlignment === 'right' ? 'justify-end' : ''}`}>
//                            {/* <span className="text-lg font-bold text-blue-600">
//                              ${product.pricingTiers && product.pricingTiers.length > 0 ?
//                                product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                                product.basePrice.toFixed(2)}
//                            </span> */}
//                            <span className="text-lg font-bold text-blue-600">
//                               {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
//                                 `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
//                               priceDisplayType === 'wholesale' && product.basePrice ?
//                                 `$${product.basePrice.toFixed(2)}` :
//                                 `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
//                             </span>

//                             {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
//                               <span className="text-xs text-green-600">
//                                 Tier price ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+)
//                               </span>
//                             )}
//                             {priceDisplayType === 'wholesale' && (
//                               <span className="text-xs text-blue-600">Wholesale price</span>
//                             )}

//                            {showQuickAction && (
//                              <div className="flex gap-3">
//                                <button
//                                  style={{
//                                    boxShadow: 'none',
//                                    border: 'none',
//                                  }}
//                                 //  onClick={(e) => {
//                                 //    e.stopPropagation();
//                                 //    navigateToProduct(product);
//                                 //  }}
//                                 onClick={(e) => {
//                                    e.stopPropagation();
//                                    handleProductClick(product)
//                                   //  navigateToProduct(product);
//                                  }}
//                                  className="text-gray-600 hover:text-blue-600 transition-colors"
//                                  title="Customize"
//                                >
//                                  {quickActionIcon === 'customize' ? (
//                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
//                                    </svg>
//                                  ) : (
//                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
//                                    </svg>
//                                  )}
//                                </button>
//                              </div>
//                            )}
//                          </div>
//                        )}
//                      </div>
//                    )}
//                  </div>
//                   ))}
//                   {getProductsByCategory(selectedCategory, categorySearchTerms[parent._id] || '').length === 0 && (
//                     <div className="col-span-full text-center py-12">
//                       <p className="text-xl text-gray-600">No products found in this category.</p>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           ))
//         ) : (
//           // If no parent categories are found, show a message
//           !loading && (
//             <div className="text-center py-12">
//               <p className="text-xl text-gray-600">No categories found.</p>
//             </div>
//           )
//         )}

//         {/* Product Detail Popup */}
//         {showProductPopup && selectedProduct && (
//           <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
//             <div
//               className={`bg-white rounded-lg shadow-xl relative max-h-screen overflow-auto ${
//                 productPopupSettings.popupSize === 'small' ? 'w-full max-w-md' :
//                 productPopupSettings.popupSize === 'medium' ? 'w-full max-w-2xl' :
//                 productPopupSettings.popupSize === 'large' ? 'w-full max-w-4xl' :
//                 'w-full max-w-7xl h-full'
//               }`}
//             >
//               {/* Close button */}
//               <button
//                 onClick={() => setShowProductPopup(false)}
//                 style={{boxShadow:'none'}}
//                 className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
//               >
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                 </svg>
//               </button>

//               <div className="flex flex-col md:flex-row">
//                 {/* Product images */}
//                 {productPopupSettings.showGallery !== false && (
//                   <div className="md:w-1/2 p-4">
//                     <div className="bg-gray-100 rounded-lg overflow-hidden">
//                       {selectedProduct.images && selectedProduct.images.length > 0 ? (
//                         <LazyImage
//                           src={selectedProduct.images[0]?.data}
//                           alt={selectedProduct.name}
//                           className="w-full h-auto object-contain aspect-square"
//                         />
//                       ) : (
//                         <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
//                           No image available
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 )}

//                 {/* Product details */}
//                 <div className={`${productPopupSettings.showGallery !== false ? 'md:w-1/2' : 'w-full'} p-6`}>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedProduct.name}</h2>

//                   {/* {productPopupSettings.showPrice !== false && (
//                     <div className="text-xl font-bold text-blue-600 mb-4">
//                       ${selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
//                         selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2) :
//                         selectedProduct.basePrice.toFixed(2)}
//                     </div>
//                   )} */}
//                   {productDisplayFields.showPrice && (
//   <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
//     <span className="text-lg font-bold text-blue-600">
//       {priceDisplayType === 'tier' && selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
//         `$${selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2)}` :
//       priceDisplayType === 'wholesale' && selectedProduct.basePrice ?
//         `$${selectedProduct.basePrice.toFixed(2)}` :
//         `$${selectedProduct.regularPrice ? selectedProduct.regularPrice.toFixed(2) : selectedProduct.basePrice.toFixed(2)}`}
//     </span>

//     {priceDisplayType === 'tier' && selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 && (
//       <span className="text-xs text-green-600">
//         Tier price ({selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].minQuantity}+)
//       </span>
//     )}
//     {priceDisplayType === 'wholesale' && (
//       <span className="text-xs text-blue-600">Wholesale price</span>
//     )}
//   </div>
// )}

//                   {productPopupSettings.showDescription !== false && selectedProduct.description && (
//                     <div className="mb-4">
//                       <h3 className="font-medium text-gray-700 mb-1">Description</h3>
//                       <p className="text-gray-600">{selectedProduct.description}</p>
//                     </div>
//                   )}

//                   {productPopupSettings.showNote !== false && selectedProduct.note && (
//                     <div className="mb-4">
//                       <h3 className="font-medium text-gray-700 mb-1">Note</h3>
//                       <p className="text-gray-600 italic">{selectedProduct.note}</p>
//                     </div>
//                   )}

//                   {productPopupSettings.showAttributes !== false && selectedProduct.attributes && selectedProduct.attributes.length > 0 && (
//                     <div className="mb-6">
//                       <h3 className="font-medium text-gray-700 mb-2">Specifications</h3>
//                       <div className="grid grid-cols-2 gap-2">
//                         {selectedProduct.attributes.map((attr, index) => (
//                           <div key={index} className="flex flex-col">
//                             <span className="text-sm text-gray-500">{attr.name}</span>
//                             <span className="font-medium">{attr.value}</span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}

//                   {/* Action buttons */}
//                   <div className="flex flex-wrap gap-4 mt-6">
//                     {productPopupSettings.showCustomizeButton !== false && (
//                       <button
//                         onClick={() => navigateToProduct(selectedProduct)}
//                         className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
//                       >
//                         Customize
//                       </button>
//                     )}

//                     {productPopupSettings.showAddToCart !== false && (
//                        <div className="mt-2 flex items-center justify-between">
//                        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              if (currentQty > 1) {
//                                setProductQuantities({
//                                  ...productQuantities,
//                                  [selectedProduct._id]: currentQty - 1
//                                });
//                              }
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            -
//                          </button>
//                          <input
//                            type="number"
//                            min="1"
//                            value={productQuantities[selectedProduct._id] || defaultQuantity}
//                            onChange={(e) => {
//                              e.stopPropagation();
//                              const newQty = parseInt(e.target.value, 10) || 1;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: newQty
//                              });
//                            }}
//                            className="w-12 text-center border-x border-gray-200 py-1"
//                          />
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: currentQty + 1
//                              });
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            +
//                          </button>
//                          <button
//                             onClick={() => {
//                               handleAddToCart(selectedProduct);
//                               setShowProductPopup(false);
//                             }}
//                             className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
//                          >
//                             Add to Cart
//                          </button>
//                        </div>
//                      </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//       </div>

//       <style>{`
//         .hide-scrollbar::-webkit-scrollbar {
//           display: none;
//         }
//         .hide-scrollbar {
//           -ms-overflow-style: none;
//           scrollbar-width: none;
//         }

//         @keyframes slide-in {
//           from {
//             transform: translateX(100%);
//             opacity: 0;
//           }
//           to {
//             transform: translateX(0);
//             opacity: 1;
//           }
//         }

//         .animate-slide-in {
//           animation: slide-in 0.3s ease-out forwards;
//         }

//         @keyframes notification-animation {
//           0% { opacity: 0; transform: scale(0.8); }
//           10% { opacity: 1; transform: scale(1.1); }
//           20% { transform: scale(1); }
//           80% { opacity: 1; transform: scale(1); }
//           100% { opacity: 0; transform: scale(0.8); }
//         }

//         .animate-notification {
//           animation: notification-animation 2s ease-in-out forwards;
//         }

//         @keyframes rotate-y {
//           from { transform: rotateY(0deg); }
//           to { transform: rotateY(180deg); }
//         }

//         .hover\:rotate-y-180:hover {
//           transform: rotateY(180deg);
//           transition: transform 0.5s;
//         }

//         .hover\:shadow-glow:hover {
//           box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.5);
//           transition: box-shadow 0.3s;
//         }
//         .fixed {
//           animation: fadeIn 0.3s ease-out;
//         }

//         @keyframes fadeIn {
//           from { opacity: 0; }
//           to { opacity: 1; }
//         }

//         /* Make the popup responsive */
//         @media (max-width: 640px) {
//           .md\\:flex-row {
//             flex-direction: column;
//           }

//           .md\\:w-1/2 {
//             width: 100%;
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// export default ProductCatalogSection;

// // do not remove this version
// // working with price tireing bounds
// // src/components/admin/page-sections/ProductCatalogSection.js
// import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
// import { useCart } from '../../../context/CartContext';
// import Lottie from 'lottie-react';
// import cartAnimation from '../../../assets/cartAnimation.json';
// import LazyImage from '../../LazyImage';

// const ProductCatalogSection = ({ content }) => {
//   const {
//     title = '',
//     description = '',
//     showSearch = true,
//     searchPlaceholder = 'Search between all categories...',
//     productsPerRow = 3,
//     showCategorySlider = true,
//     categoryItemSize = 'medium',
//     categoryBorderColor = '#3B82F6',
//     selectedCategoryBorderColor = '#F59E0B',
//     productCardStyle = 'shadow',
//     showQuickAction = true,
//     quickActionIcon = 'customize',
//     backgroundColor = '#F9FAFB',
//     initialCategorySlug = '',
//     categoryApiPath = '/api/categories',
//     productApiPath = '/api/products/basic',
//     // New category display options
//     categoryDisplayMode = 'slider',
//     categoryShape = 'circle',
//     categoryTextPosition = 'below',
//     categoryOverlayOpacity = '0.7',
//     categoryTextColor = '#ffffff',
//     categoryHoverEffect = 'zoom',
//     // New product display options
//     productDisplayFields = {
//       showName: true,
//       showPrice: true,
//       showDescription: false,
//       showNote: false,
//       showRating: false
//     },
//     productInfoPosition = 'below',
//     productTextAlignment = 'left',
//     productNameLines = 2,
//     productDescriptionLines = 3,
//     productImageAspectRatio = 'square',
//     productClickBehavior = 'navigate',
//     productPopupSettings = {
//       showGallery: true,
//       showDescription: true,
//       showPrice: true,
//       showNote: true,
//       showAttributes: true,
//       showAddToCart: true,
//       showCustomizeButton: true,
//       popupSize: 'medium'
//     },
//     priceDisplayType = 'regular',
//     allowQuantitySelection = false,
//     defaultQuantity = 1,
//   } = content;

//   const [productQuantities, setProductQuantities] = useState({});
//   const [categories, setCategories] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [parentCategories, setParentCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [mainSearchTerm, setMainSearchTerm] = useState('');
//   const [categorySearchTerms, setCategorySearchTerms] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [showCartNotification, setShowCartNotification] = useState(false);
//   const { addToCart } = useCart();
//   const sliderRefs = useRef({});

//   //for product navigation or popup
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [showProductPopup, setShowProductPopup] = useState(false);

//   const selectedCategoryData = categories.find(cat => cat._id === selectedCategory);

//   // Parse grid columns value based on number of products per row
//   const gridCols = {
//     1: 'grid-cols-1',
//     2: 'grid-cols-1 md:grid-cols-2',
//     3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
//     4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
//   };

//   // Set category item size based on admin choice
//   const categoryItemSizes = {
//     small: 'w-32 h-32',
//     medium: 'w-48 h-48',
//     large: 'w-64 h-64'
//   };

//   // Product card style options
//   const cardStyles = {
//     shadow: 'shadow-lg',
//     outline: 'border border-gray-200',
//     flat: ''
//   };

//   // Category shape styles
//   const categoryShapeStyles = {
//     circle: 'rounded-full',
//     rectangle: 'rounded-md aspect-[16/9]',
//     square: 'rounded-md aspect-square',
//     pill: 'rounded-full aspect-[3/1]'
//   };

//   // Category hover effect styles
//   const categoryHoverStyles = {
//     zoom: 'hover:scale-105',
//     flip: 'hover:rotate-y-180',
//     fade: 'hover:opacity-70',
//     glow: 'hover:shadow-glow',
//     none: ''
//   };

//   // Product image aspect ratio styles
//   const productImageRatios = {
//     square: 'aspect-square',
//     portrait: 'aspect-[3/4]',
//     landscape: 'aspect-[4/3]',
//     original: 'aspect-auto'
//   };

//   // Product info position layout styles
//   const productInfoStyles = {
//     below: 'flex flex-col',
//     right: 'flex flex-row',
//     left: 'flex flex-row-reverse',
//     overlay: 'relative'
//   };

//   // Function to create URL-friendly slugs
//   const createSlug = (name) => {
//     return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
//   };

//   // Function to get category URL for linking
//   const getCategoryUrl = (category) => {
//     return `/cat/${createSlug(category.name)}`;
//   };

//   // Function to handle product click
//   const handleProductClick = (product) => {
//     if (productClickBehavior === 'popup') {
//       setSelectedProduct(product);
//       setShowProductPopup(true);
//     } else {
//       navigateToProduct(product);
//     }
//   };

//   // Function to navigate to product page
//   const navigateToProduct = (product) => {
//     const match = product.name.match(/^(.*?)(\d.*)?$/);
//     let baseName = match[1] ? match[1].trim() : product.name;
//     let sizePart = match[2] ? match[2].trim() : '';

//     const slugBase = baseName
//       .toLowerCase()
//       .replace(/\s+/g, '-')
//       .replace(/[^a-z0-9-]/g, '');

//     const slugSize = sizePart;
//     const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

//     window.location.href = `/product/${productSlug}`;
//   };

//   // Function to set category from URL or click
//   const navigateToCategory = (category) => {
//     const categorySlug = createSlug(category.name);
//     window.history.pushState({}, '', `/cat/${categorySlug}`);
//     setSelectedCategory(category._id);
//   };

//   // Function to scroll category sliders
//   const scroll = (direction, parentId) => {
//     const container = sliderRefs.current[parentId];
//     if (container) {
//       const scrollAmount = direction === 'left' ? -300 : 300;
//       container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
//     }
//   };

//   // Handle category search input
//   const handleSearchTermChange = (parentId, term) => {
//     setCategorySearchTerms(prev => ({
//       ...prev,
//       [parentId]: term
//     }));
//   };

//   // Get categories by parent ID
//   const getCategoriesByParent = (parentId) => {
//     return categories.filter(cat =>
//       cat.parentCategory &&
//       (cat.parentCategory._id === parentId || cat.parentCategory === parentId)
//     );
//   };

//   // Get products for a specific category
//   const getProductsByCategory = (categoryId, searchTerm = '') => {
//     return products.filter(product => {
//       const productCatId = product.category?._id?.toString ?
//         product.category._id.toString() :
//         product.category?._id;

//       const targetCatId = categoryId?.toString ? categoryId.toString() : categoryId;

//       return productCatId === targetCatId &&
//         (product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//          product.description.toLowerCase().includes(searchTerm.toLowerCase()));
//     });
//   };

//   // Add this helper function to get pricing tiers for a product
//   const getProductTiers = (product) => {
//     if (!product.pricingTiers || product.pricingTiers.length === 0) {
//       return [];
//     }
//     return [...product.pricingTiers].sort((a, b) => a.minQuantity - b.minQuantity);
//   };

//   // Add product to cart
//   // const handleAddToCart = async (product) => {
//   //   try {
//   //     const token = localStorage.getItem('token');
//   //     if (!token) {
//   //       window.location.href = '/login';
//   //       return;
//   //     }

//   //     await addToCart({
//   //       product,
//   //       quantity: 1,
//   //       customization: {
//   //         preview: product.images[0]?.data || '',
//   //         description: ''
//   //       }
//   //     });

//   //     // Show notification and scroll to top
//   //     setShowCartNotification(true);
//   //     window.scrollTo({ top: 0, behavior: 'smooth' });
//   //     setTimeout(() => setShowCartNotification(false), 2000);
//   //   } catch (error) {
//   //     console.error('Error adding to cart:', error);
//   //   }
//   // };
//   // Update the handleAddToCart function to use the quantity
//   const handleAddToCart = async (product) => {
//     try {
//       const token = localStorage.getItem('token');
//       if (!token) {
//         window.location.href = '/login';
//         return;
//       }

//       // Use the selected quantity or default
//       const quantity = productQuantities[product._id] || defaultQuantity || 1;

//       await addToCart({
//         product,
//         quantity: quantity,
//         customization: {
//           preview: product.images[0]?.data || '',
//           description: ''
//         }
//       });

//       // Show notification and scroll to top
//       setShowCartNotification(true);
//       window.scrollTo({ top: 0, behavior: 'smooth' });
//       setTimeout(() => setShowCartNotification(false), 2000);
//     } catch (error) {
//       console.error('Error adding to cart:', error);
//     }
//   };

//   // Load categories and products data
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         // First fetch basic data without images (fast)
//         const [categoriesRes, productsRes] = await Promise.all([
//           fetch(categoryApiPath).then(res => res.json()),
//           fetch(productApiPath).then(res => res.json())
//         ]);

//         setCategories(categoriesRes);
//         setProducts(productsRes);

//         // Fetch parent categories
//         const parentCatRes = await fetch('/api/parent-categories');
//         if (parentCatRes.ok) {
//           const data = await parentCatRes.json();
//           setParentCategories(data);

//           // Initialize search terms for each parent category
//           const initialSearchTerms = {};
//           data.forEach(parent => {
//             initialSearchTerms[parent._id] = '';
//           });
//           setCategorySearchTerms(initialSearchTerms);
//         }

//         // Check for initial category selection from URL or props
//         const pathname = window.location.pathname;
//         const urlCategorySlug = pathname.startsWith('/cat/') ? pathname.split('/cat/')[1] : '';
//         const categorySlug = initialCategorySlug || urlCategorySlug;

//         if (categorySlug) {
//           const matchingCategory = categoriesRes.find(cat =>
//             createSlug(cat.name) === categorySlug
//           );

//           if (matchingCategory) {
//             setSelectedCategory(matchingCategory._id);
//           }
//         }

//         setLoading(false);

//         // Then fetch images in parallel
//         const categoryImagePromises = categoriesRes.map(category =>
//           fetch(`/api/categories/${category._id}/image`)
//             .then(res => res.json())
//             .then(imageData => {
//               setCategories(prev => prev.map(c =>
//                 c._id === category._id ? { ...c, image: imageData } : c
//               ));
//             })
//             .catch(error => console.error(`Error loading image for category ${category._id}:`, error))
//         );

//         const productImagePromises = productsRes.map(product =>
//           fetch(`/api/products/${product._id}/image`)
//             .then(res => res.json())
//             .then(imageData => {
//               setProducts(prev => prev.map(p =>
//                 p._id === product._id ? { ...p, images: [imageData] } : p
//               ));
//             })
//             .catch(error => console.error(`Error loading image for product ${product._id}:`, error))
//         );

//         // Load all images in parallel
//         await Promise.allSettled([...categoryImagePromises, ...productImagePromises]);
//       } catch (error) {
//         console.error('Error fetching data:', error);
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [categoryApiPath, productApiPath, initialCategorySlug]);

//   // // Add this useEffect to initialize quantities based on price type
//   // useEffect(() => {
//   //   // Initialize with default quantities when products load
//   //   if (products.length > 0) {
//   //     const initialQuantities = {};
//   //     products.forEach(product => {
//   //       let defaultQty = defaultQuantity;
//   //       if (priceDisplayType === 'wholesale' && !defaultQty) {
//   //         defaultQty = 50;
//   //       } else if (priceDisplayType === 'regular' && !defaultQty) {
//   //         defaultQty = 1;
//   //       }
//   //       initialQuantities[product._id] = defaultQty;
//   //     });
//   //     setProductQuantities(initialQuantities);
//   //   }
//   // }, [products, defaultQuantity, priceDisplayType]);
// // Add this useEffect to initialize quantities based on price type
// useEffect(() => {
//   // Initialize with default quantities when products load
//   if (products.length > 0) {
//     const initialQuantities = {};
//     products.forEach(product => {
//       if (priceDisplayType === 'tier' && product.pricingTiers?.length > 0) {
//         // For tier pricing, use the first tier's minimum quantity
//         const sortedTiers = [...product.pricingTiers].sort((a, b) => a.minQuantity - b.minQuantity);
//         initialQuantities[product._id] = sortedTiers[0].minQuantity;
//       } else if (priceDisplayType === 'wholesale' && !defaultQuantity) {
//         // For wholesale pricing without a specified default
//         initialQuantities[product._id] = 50;
//       } else if (priceDisplayType === 'regular' && !defaultQuantity) {
//         // For regular pricing without a specified default
//         initialQuantities[product._id] = 1;
//       } else {
//         // Use the configured default quantity
//         initialQuantities[product._id] = defaultQuantity;
//       }
//     });
//     setProductQuantities(initialQuantities);
//   }
// }, [products, defaultQuantity, priceDisplayType]);

//   return (
//     <div className="min-h-screen" style={{ backgroundColor }}>
//       {/* Cart Notification */}
//       {showCartNotification && (
//         <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
//           <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
//             <Lottie animationData={cartAnimation} loop={false} className="h-24 w-24" />
//             <p className="text-xl">Item added to cart successfully!</p>
//           </div>
//         </div>
//       )}

//       <div className="max-w-7xl mx-auto px-4 py-8">
//         {/* Section Header */}
//         {(title || description) && (
//           <div className="mb-8 text-center">
//             {title && <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>}
//             {description && <p className="text-lg text-gray-600">{description}</p>}
//           </div>
//         )}

//         {/* Main Search Bar */}
//         {showSearch && (
//           <div className="mb-12">
//             <div className="max-w-3xl mx-auto">
//               <div className="relative">
//                 <input
//                   type="text"
//                   placeholder={searchPlaceholder}
//                   value={mainSearchTerm}
//                   onChange={(e) => setMainSearchTerm(e.target.value)}
//                   className="w-full px-4 py-4 pl-12 rounded-xl border-2 border-blue-500 focus:outline-none focus:border-blue-600 text-lg"
//                 />
//                 <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Parent Categories Section */}
//         {parentCategories.length > 0 ? (
//           // If we have parent categories, map through them
//           parentCategories.map(parent => (
//             <div key={parent._id} className="mb-16">
//               <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
//                 <h2 className="text-3xl font-bold text-gray-900">{parent.name}</h2>
//                 {showSearch && (
//                   <div className="relative w-full md:w-64">
//                     <input
//                       type="text"
//                       placeholder={`Search ${parent.name.toLowerCase()}...`}
//                       value={categorySearchTerms[parent._id] || ''}
//                       onChange={(e) => handleSearchTermChange(parent._id, e.target.value)}
//                       className="w-full px-4 py-2 pl-10 rounded-full border-2 border-blue-500 focus:outline-none focus:border-blue-600"
//                     />
//                     <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
//                   </div>
//                 )}
//               </div>

//               {/* Category display based on mode */}
//               {showCategorySlider && (
//                  <>
//                  {categoryDisplayMode === 'slider' && (
//                   <div className="relative mb-12">
//                   <button
//                     style={{transform: 'none'}}
//                     onClick={() => scroll('left', parent._id)}
//                     className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                   >
//                     <ChevronLeft size={32} />
//                   </button>

//                   <div
//                     ref={el => sliderRefs.current[parent._id] = el}
//                     className="flex overflow-x-auto hide-scrollbar gap-12 px-16 py-4"
//                     style={{paddingTop: 15+'px'}}
//                   >
//                     {getCategoriesByParent(parent._id).map((category) => (
//                       <div
//                         key={category._id}
//                         onClick={() => navigateToCategory(category)}
//                         className={`flex-none cursor-pointer transition-transform ${
//                           categoryHoverStyles[categoryHoverEffect]
//                         } ${selectedCategory === category._id ? 'scale-105' : ''}`}
//                       >
//                         <div
//                           className={`${categoryItemSizes[categoryItemSize]} overflow-hidden border-4 mb-4 ${categoryShapeStyles[categoryShape]} relative`}
//                           style={{
//                             borderColor: selectedCategory === category._id
//                               ? content.selectedCategoryBorderColor || '#F59E0B'
//                               : content.categoryBorderColor || '#3B82F6'
//                           }}
//                         >
//                           <LazyImage
//                             src={category.image?.data}
//                             alt={category.name}
//                             className="w-full h-full object-cover"
//                           />

//                           {categoryTextPosition === 'overlay' && (
//                             <div
//                               className="absolute inset-0 flex items-center justify-center"
//                               style={{
//                                 backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                 color: categoryTextColor
//                               }}
//                             >
//                               <p className="text-center font-medium text-lg px-2">{category.name}</p>
//                             </div>
//                           )}
//                         </div>

//                         {categoryTextPosition === 'below' && (
//                           <p className="text-center font-medium text-gray-800 text-lg max-w-[180px] mx-auto">{category.name}</p>
//                         )}
//                         <div className="text-center mt-2">
//                           <a
//                             href={getCategoryUrl(category)}
//                             className="text-blue-600 hover:text-blue-800 text-sm font-medium"
//                             onClick={(e) => e.stopPropagation()}
//                           >
//                             {/* View Products */}
//                           </a>
//                         </div>
//                       </div>
//                     ))}
//                   </div>

//                   <button
//                     onClick={() => scroll('right', parent._id)}
//                     style={{transform: 'none'}}
//                     className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                   >
//                     <ChevronRight size={32} />
//                   </button>
//                   </div>
//                  )}

//                  {categoryDisplayMode === 'grid' && (
//                   <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
//                   {getCategoriesByParent(parent._id).map((category) => (
//                     <div
//                       key={category._id}
//                       onClick={() => navigateToCategory(category)}
//                       className={`cursor-pointer transition-transform ${
//                         categoryHoverStyles[categoryHoverEffect]
//                       } ${selectedCategory === category._id ? 'scale-105' : ''}`}
//                     >
//                       <div
//                         className={`w-full aspect-square overflow-hidden border-4 mb-2 ${categoryShapeStyles[categoryShape]} relative`}
//                         style={{
//                           borderColor: selectedCategory === category._id
//                             ? content.selectedCategoryBorderColor || '#F59E0B'
//                             : content.categoryBorderColor || '#3B82F6'
//                         }}
//                       >
//                         <LazyImage
//                           src={category.image?.data}
//                           alt={category.name}
//                           className="w-full h-full object-cover"
//                         />

//                         {categoryTextPosition === 'overlay' && (
//                           <div
//                             className="absolute inset-0 flex items-center justify-center"
//                             style={{
//                               backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                               color: categoryTextColor
//                             }}
//                           >
//                             <p className="text-center font-medium text-lg px-2">{category.name}</p>
//                           </div>
//                         )}
//                       </div>

//                       {categoryTextPosition === 'below' && (
//                         <p className="text-center font-medium text-gray-800">{category.name}</p>
//                       )}
//                     </div>
//                   ))}
//                   </div>
//                  )}

//                  {categoryDisplayMode === 'fullWidth' && (
//                     <div className="relative mb-12">
//                     <button
//                       onClick={() => {
//                         const categories = getCategoriesByParent(parent._id);
//                         if (categories.length === 0) return;

//                         if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                           // If no category is selected or selected category is from another parent,
//                           // select the first category from this parent
//                           navigateToCategory(categories[0]);
//                         } else {
//                           // Find current index and navigate to previous
//                           const currentIndex = categories.findIndex(c => c._id === selectedCategory);
//                           const prevIndex = currentIndex > 0 ? currentIndex - 1 : categories.length - 1;
//                           navigateToCategory(categories[prevIndex]);
//                         }
//                       }}
//                       style={{transform: 'none'}}
//                       className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                     >
//                       <ChevronLeft size={32} />
//                     </button>

//                     {(() => {
//                       const categories = getCategoriesByParent(parent._id);

//                       // If no category is selected or selected category is from another parent,
//                       // show the first category from this parent
//                       if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                         const category = categories[0];
//                         if (!category) return null;

//                         return (
//                           <div
//                             key={category._id}
//                             className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
//                             onClick={() => navigateToCategory(category)}
//                           >
//                             <LazyImage
//                               src={category.image?.data}
//                               alt={category.name}
//                               className="w-full h-full object-cover"
//                             />

//                             {categoryTextPosition === 'overlay' && (
//                               <div
//                                 className="absolute inset-0 flex items-center justify-center"
//                                 style={{
//                                   backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                   color: categoryTextColor
//                                 }}
//                               >
//                                 <h3 className="text-3xl font-bold px-4 text-center">{category.name}</h3>
//                               </div>
//                             )}

//                             {categoryTextPosition === 'below' && (
//                               <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
//                                 <h3 className="text-xl font-bold text-center">{category.name}</h3>
//                               </div>
//                             )}
//                           </div>
//                         );
//                       }

//                       // Otherwise, show the selected category
//                       return getCategoriesByParent(parent._id).map((category) => (
//                         selectedCategory === category._id && (
//                           <div
//                             key={category._id}
//                             className={`w-full h-56 md:h-80 relative overflow-hidden rounded-lg ${categoryHoverStyles[categoryHoverEffect]} cursor-pointer`}
//                             onClick={() => navigateToCategory(category)}
//                           >
//                             <LazyImage
//                               src={category.image?.data}
//                               alt={category.name}
//                               className="w-full h-full object-cover"
//                             />

//                             {categoryTextPosition === 'overlay' && (
//                               <div
//                                 className="absolute inset-0 flex items-center justify-center"
//                                 style={{
//                                   backgroundColor: `rgba(0,0,0,${categoryOverlayOpacity})`,
//                                   color: categoryTextColor
//                                 }}
//                               >
//                                 <h3 className="text-3xl font-bold px-4 text-center">{category.name}</h3>
//                               </div>
//                             )}

//                             {categoryTextPosition === 'below' && (
//                               <div className="absolute bottom-0 left-0 right-0 bg-white bg-opacity-80 p-4">
//                                 <h3 className="text-xl font-bold text-center">{category.name}</h3>
//                               </div>
//                             )}
//                           </div>
//                         )
//                       ));
//                     })()}

//                     <button
//                       onClick={() => {
//                         const categories = getCategoriesByParent(parent._id);
//                         if (categories.length === 0) return;

//                         if (!selectedCategory || !categories.some(c => c._id === selectedCategory)) {
//                           // If no category is selected or selected category is from another parent,
//                           // select the second category (or first if only one exists)
//                           navigateToCategory(categories[Math.min(1, categories.length - 1)]);
//                         } else {
//                           // Find current index and navigate to next
//                           const currentIndex = categories.findIndex(c => c._id === selectedCategory);
//                           const nextIndex = currentIndex < categories.length - 1 ? currentIndex + 1 : 0;
//                           navigateToCategory(categories[nextIndex]);
//                         }
//                       }}
//                       style={{transform: 'none'}}
//                       className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//                     >
//                       <ChevronRight size={32} />
//                     </button>
//                   </div>
//                  )}
//                </>
//              )}

//               {/* Products Grid for Selected Category within this parent */}
//               {selectedCategory && getCategoriesByParent(parent._id).some(cat => cat._id === selectedCategory) && (
//                 <div className={`grid ${gridCols[productsPerRow]} gap-8 mt-8 mx-8`}>
//                   {getProductsByCategory(selectedCategory, categorySearchTerms[parent._id] || '').map((product) => (
//                    <div
//                    key={product._id}
//                    className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
//                  >
//                    {/* Product image */}
//                    <div
//                      className={`relative cursor-pointer ${
//                        productInfoPosition === 'below' ? 'w-full' :
//                        productInfoPosition === 'right' || productInfoPosition === 'left' ? 'w-1/2' : 'w-full'
//                      } ${productImageRatios[productImageAspectRatio]}`}
//                     //  onClick={() => navigateToProduct(product)}
//                     onClick={() => handleProductClick(product)}

//                    >
//                      <LazyImage
//                        src={product.images?.[0]?.data}
//                        alt={product.name}
//                        className="w-full h-full object-cover"
//                      />

//                      {/* Overlay product info if that position is selected */}
//                      {productInfoPosition === 'overlay' && (
//                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-4">
//                          {productDisplayFields.showName && (
//                            <h3
//                              className={`font-bold mb-1 text-${productTextAlignment}`}
//                              style={{
//                                WebkitLineClamp: productNameLines > 0 ? productNameLines : 'none',
//                                display: productNameLines > 0 ? '-webkit-box' : 'block',
//                                WebkitBoxOrient: 'vertical',
//                                overflow: 'hidden'
//                              }}
//                            >
//                              {product.name}
//                            </h3>
//                          )}

//                          {productDisplayFields.showPrice && (
//                           //  <div className={`text-${productTextAlignment} font-bold text-yellow-400 mb-1`}>
//                           //    ${product.pricingTiers && product.pricingTiers.length > 0 ?
//                           //      product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                           //      product.basePrice.toFixed(2)}
//                           //  </div>
//                           <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
//                             <span className="text-lg font-bold text-blue-600">
//                               {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
//                                 `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
//                               priceDisplayType === 'wholesale' && product.basePrice ?
//                                 `$${product.basePrice.toFixed(2)}` :
//                                 `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
//                             </span>

//                             {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
//                               <span className="text-xs text-green-600">
//                                 Tier price ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+)
//                               </span>
//                             )}
//                             {priceDisplayType === 'wholesale' && (
//                               <span className="text-xs text-blue-600">Wholesale price</span>
//                             )}
//                           </div>
//                          )}

//                          {productDisplayFields.showDescription && product.description && (
//                            <p
//                              className={`text-sm text-gray-200 text-${productTextAlignment}`}
//                              style={{
//                                WebkitLineClamp: productDescriptionLines > 0 ? productDescriptionLines : 'none',
//                                display: productDescriptionLines > 0 ? '-webkit-box' : 'block',
//                                WebkitBoxOrient: 'vertical',
//                                overflow: 'hidden'
//                              }}
//                            >
//                              {product.description}
//                            </p>
//                          )}
//                        </div>
//                      )}
//                    </div>

//                    {/* Product info not in overlay position */}
//                    {productInfoPosition !== 'overlay' && (
//                      <div
//                        className={`p-4 cursor-pointer ${
//                          productInfoPosition === 'below' ? 'w-full' :
//                          productInfoPosition === 'right' || productInfoPosition === 'left' ? 'w-1/2 flex flex-col justify-center' : ''
//                        }`}
//                       //  onClick={() => navigateToProduct(product)}
//                       onClick={() => handleProductClick(product)}

//                      >
//                        {productDisplayFields.showName && (
//                          <h3
//                            className={`font-bold mb-2 text-gray-900 text-${productTextAlignment}`}
//                            style={{
//                              WebkitLineClamp: productNameLines > 0 ? productNameLines : 'none',
//                              display: productNameLines > 0 ? '-webkit-box' : 'block',
//                              WebkitBoxOrient: 'vertical',
//                              overflow: 'hidden'
//                            }}
//                          >
//                            {product.name}
//                          </h3>
//                        )}

//                        {productDisplayFields.showDescription && product.description && (
//                          <p
//                            className={`text-sm text-gray-600 mb-2 text-${productTextAlignment}`}
//                            style={{
//                              WebkitLineClamp: productDescriptionLines > 0 ? productDescriptionLines : 'none',
//                              display: productDescriptionLines > 0 ? '-webkit-box' : 'block',
//                              WebkitBoxOrient: 'vertical',
//                              overflow: 'hidden'
//                            }}
//                          >
//                            {product.description}
//                          </p>
//                        )}

//                        {productDisplayFields.showNote && product.note && (
//                          <p className={`text-sm italic text-gray-500 mb-2 text-${productTextAlignment}`}>
//                            {product.note}
//                          </p>
//                        )}

//                        {/* {productDisplayFields.showRating && (
//                          <div className={`flex ${productTextAlignment === 'center' ? 'justify-center' : productTextAlignment === 'right' ? 'justify-end' : ''} mb-2`}>
//                            {[1, 2, 3, 4, 5].map((star) => (
//                              <Star
//                                key={star}
//                                size={16}
//                                className={star <= (product.rating || 5) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}
//                              />
//                            ))}
//                          </div>
//                        )} */}

//                        {productDisplayFields.showPrice && (
//                          <div className={`flex items-center justify-between ${productTextAlignment === 'center' ? 'justify-center' : productTextAlignment === 'right' ? 'justify-end' : ''}`}>
//                            {/* <span className="text-lg font-bold text-blue-600">
//                              ${product.pricingTiers && product.pricingTiers.length > 0 ?
//                                product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                                product.basePrice.toFixed(2)}
//                            </span> */}
//                            <span className="text-lg font-bold text-blue-600">
//                               {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
//                                 `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
//                               priceDisplayType === 'wholesale' && product.basePrice ?
//                                 `$${product.basePrice.toFixed(2)}` :
//                                 `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
//                             </span>

//                             {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
//                               <span className="text-xs text-green-600">
//                                 Tier price ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+)
//                               </span>
//                             )}
//                             {priceDisplayType === 'wholesale' && (
//                               <span className="text-xs text-blue-600">Wholesale price</span>
//                             )}

//                            {showQuickAction && (
//                              <div className="flex gap-3">
//                                <button
//                                  style={{
//                                    boxShadow: 'none',
//                                    border: 'none',
//                                  }}
//                                 //  onClick={(e) => {
//                                 //    e.stopPropagation();
//                                 //    navigateToProduct(product);
//                                 //  }}
//                                 onClick={(e) => {
//                                    e.stopPropagation();
//                                    handleProductClick(product)
//                                   //  navigateToProduct(product);
//                                  }}
//                                  className="text-gray-600 hover:text-blue-600 transition-colors"
//                                  title="Customize"
//                                >
//                                  {quickActionIcon === 'customize' ? (
//                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
//                                    </svg>
//                                  ) : (
//                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
//                                    </svg>
//                                  )}
//                                </button>
//                              </div>
//                            )}
//                          </div>
//                        )}
//                      </div>
//                    )}
//                  </div>
//                   ))}
//                   {getProductsByCategory(selectedCategory, categorySearchTerms[parent._id] || '').length === 0 && (
//                     <div className="col-span-full text-center py-12">
//                       <p className="text-xl text-gray-600">No products found in this category.</p>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           ))
//         ) : (
//           // If no parent categories are found, show a message
//           !loading && (
//             <div className="text-center py-12">
//               <p className="text-xl text-gray-600">No categories found.</p>
//             </div>
//           )
//         )}

//         {/* Show message when no category is selected */}
//         {!selectedCategory && !loading && (
//           <div className="text-center py-12">
//             <p className="text-xl text-gray-600">Select a category to view products.</p>
//           </div>
//         )}

//         {/* Loading indicator */}
//         {loading && (
//           // <div className="text-center py-12">
//           //   <p className="text-xl text-gray-600">Loading products...</p>
//           // </div>
//           <div className="fixed inset-0 flex flex-col items-center justify-center">
//           {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
//           <img src='../images/loading.gif'/>
//         </div>
//         )}

//         {/* Product Detail Popup */}
//         {showProductPopup && selectedProduct && (
//           <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
//             <div
//               className={`bg-white rounded-lg shadow-xl relative max-h-screen overflow-auto ${
//                 productPopupSettings.popupSize === 'small' ? 'w-full max-w-md' :
//                 productPopupSettings.popupSize === 'medium' ? 'w-full max-w-2xl' :
//                 productPopupSettings.popupSize === 'large' ? 'w-full max-w-4xl' :
//                 'w-full max-w-7xl h-full'
//               }`}
//             >
//               {/* Close button */}
//               <button
//                 onClick={() => setShowProductPopup(false)}
//                 style={{boxShadow:'none'}}
//                 className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
//               >
//                 <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                 </svg>
//               </button>

//               <div className="flex flex-col md:flex-row">
//                 {/* Product images */}
//                 {productPopupSettings.showGallery !== false && (
//                   <div className="md:w-1/2 p-4">
//                     <div className="bg-gray-100 rounded-lg overflow-hidden">
//                       {selectedProduct.images && selectedProduct.images.length > 0 ? (
//                         <LazyImage
//                           src={selectedProduct.images[0]?.data}
//                           alt={selectedProduct.name}
//                           className="w-full h-auto object-contain aspect-square"
//                         />
//                       ) : (
//                         <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
//                           No image available
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 )}

//                 {/* Product details */}
//                 <div className={`${productPopupSettings.showGallery !== false ? 'md:w-1/2' : 'w-full'} p-6`}>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedProduct.name}</h2>

//                   {/* {productPopupSettings.showPrice !== false && (
//                     <div className="text-xl font-bold text-blue-600 mb-4">
//                       ${selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
//                         selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2) :
//                         selectedProduct.basePrice.toFixed(2)}
//                     </div>
//                   )} */}
//                   {productDisplayFields.showPrice && (
//   <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
//     <span className="text-lg font-bold text-blue-600">
//       {priceDisplayType === 'tier' && selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
//         `$${selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2)}` :
//       priceDisplayType === 'wholesale' && selectedProduct.basePrice ?
//         `$${selectedProduct.basePrice.toFixed(2)}` :
//         `$${selectedProduct.regularPrice ? selectedProduct.regularPrice.toFixed(2) : selectedProduct.basePrice.toFixed(2)}`}
//     </span>

//     {priceDisplayType === 'tier' && selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 && (
//       <span className="text-xs text-green-600">
//         Tier price ({selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].minQuantity}+)
//       </span>
//     )}
//     {priceDisplayType === 'wholesale' && (
//       <span className="text-xs text-blue-600">Wholesale price</span>
//     )}
//   </div>
// )}

//                   {productPopupSettings.showDescription !== false && selectedProduct.description && (
//                     <div className="mb-4">
//                       <h3 className="font-medium text-gray-700 mb-1">Description</h3>
//                       <p className="text-gray-600">{selectedProduct.description}</p>
//                     </div>
//                   )}

//                   {productPopupSettings.showNote !== false && selectedProduct.note && (
//                     <div className="mb-4">
//                       <h3 className="font-medium text-gray-700 mb-1">Note</h3>
//                       <p className="text-gray-600 italic">{selectedProduct.note}</p>
//                     </div>
//                   )}

//                   {productPopupSettings.showAttributes !== false && selectedProduct.attributes && selectedProduct.attributes.length > 0 && (
//                     <div className="mb-6">
//                       <h3 className="font-medium text-gray-700 mb-2">Specifications</h3>
//                       <div className="grid grid-cols-2 gap-2">
//                         {selectedProduct.attributes.map((attr, index) => (
//                           <div key={index} className="flex flex-col">
//                             <span className="text-sm text-gray-500">{attr.name}</span>
//                             <span className="font-medium">{attr.value}</span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}

//                   {/* Action buttons */}
//                   <div className="flex flex-wrap gap-4 mt-6">
//                     {productPopupSettings.showCustomizeButton !== false && (
//                       <button
//                         onClick={() => navigateToProduct(selectedProduct)}
//                         className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
//                       >
//                         Customize
//                       </button>
//                     )}

//                     {/* {productPopupSettings.showAddToCart !== false && (
//                        <div className="mt-2 flex items-center justify-between">
//                        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              if (currentQty > 1) {
//                                setProductQuantities({
//                                  ...productQuantities,
//                                  [selectedProduct._id]: currentQty - 1
//                                });
//                              }
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            -
//                          </button>
//                          <input
//                            type="number"
//                            min="1"
//                            value={productQuantities[selectedProduct._id] || defaultQuantity}
//                            onChange={(e) => {
//                              e.stopPropagation();
//                              const newQty = parseInt(e.target.value, 10) || 1;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: newQty
//                              });
//                            }}
//                            className="w-12 text-center border-x border-gray-200 py-1"
//                          />
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: currentQty + 1
//                              });
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            +
//                          </button>
//                          <button
//                             onClick={() => {
//                               handleAddToCart(selectedProduct);
//                               setShowProductPopup(false);
//                             }}
//                             className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
//                          >
//                             Add to Cart
//                          </button>
//                        </div>
//                      </div>
//                     )} */}
//                     // Modify the quantity selector in the product popup
// {productPopupSettings.showAddToCart !== false && (
//   <div className="mt-2 flex items-center justify-between">
//     <div className="flex items-center border border-gray-200 rounded overflow-hidden">
//       {priceDisplayType === 'tier' && selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ? (
//         // For tier pricing, use a dropdown instead of +/- buttons
//         <select
//           value={productQuantities[selectedProduct._id] ||
//             selectedProduct.pricingTiers[0].minQuantity}
//           onChange={(e) => {
//             setProductQuantities({
//               ...productQuantities,
//               [selectedProduct._id]: parseInt(e.target.value, 10)
//             });
//           }}
//           className="px-2 py-1 border-none focus:outline-none"
//         >
//           {getProductTiers(selectedProduct).map((tier, index) => (
//             <option key={index} value={tier.minQuantity}>
//               {tier.minQuantity} {tier.maxQuantity ? `- ${tier.maxQuantity}` : '+'} (${tier.price}/each)
//             </option>
//           ))}
//           <option value="20000">+20000 (Quote Request)</option>
//         </select>
//       ) : (
//         // For regular and wholesale pricing, keep +/- buttons
//         <>
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//               if (currentQty > 1) {
//                 setProductQuantities({
//                   ...productQuantities,
//                   [selectedProduct._id]: currentQty - 1
//                 });
//               }
//             }}
//             className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//           >
//             -
//           </button>
//           <input
//             type="number"
//             min="1"
//             value={productQuantities[selectedProduct._id] || defaultQuantity}
//             onChange={(e) => {
//               e.stopPropagation();
//               const newQty = parseInt(e.target.value, 10) || 1;
//               setProductQuantities({
//                 ...productQuantities,
//                 [selectedProduct._id]: newQty
//               });
//             }}
//             className="w-12 text-center border-x border-gray-200 py-1"
//           />
//           <button
//             onClick={(e) => {
//               e.stopPropagation();
//               const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//               setProductQuantities({
//                 ...productQuantities,
//                 [selectedProduct._id]: currentQty + 1
//               });
//             }}
//             className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//           >
//             +
//           </button>
//         </>
//       )}
//       <button
//         onClick={() => {
//           handleAddToCart(selectedProduct);
//           setShowProductPopup(false);
//         }}
//         className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
//       >
//         Add to Cart
//       </button>
//     </div>
//   </div>
// )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//       </div>

//       <style>{`
//         .hide-scrollbar::-webkit-scrollbar {
//           display: none;
//         }
//         .hide-scrollbar {
//           -ms-overflow-style: none;
//           scrollbar-width: none;
//         }

//         @keyframes slide-in {
//           from {
//             transform: translateX(100%);
//             opacity: 0;
//           }
//           to {
//             transform: translateX(0);
//             opacity: 1;
//           }
//         }

//         .animate-slide-in {
//           animation: slide-in 0.3s ease-out forwards;
//         }

//         @keyframes notification-animation {
//           0% { opacity: 0; transform: scale(0.8); }
//           10% { opacity: 1; transform: scale(1.1); }
//           20% { transform: scale(1); }
//           80% { opacity: 1; transform: scale(1); }
//           100% { opacity: 0; transform: scale(0.8); }
//         }

//         .animate-notification {
//           animation: notification-animation 2s ease-in-out forwards;
//         }

//         @keyframes rotate-y {
//           from { transform: rotateY(0deg); }
//           to { transform: rotateY(180deg); }
//         }

//         .hover\:rotate-y-180:hover {
//           transform: rotateY(180deg);
//           transition: transform 0.5s;
//         }

//         .hover\:shadow-glow:hover {
//           box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.5);
//           transition: box-shadow 0.3s;
//         }
//         .fixed {
//           animation: fadeIn 0.3s ease-out;
//         }

//         @keyframes fadeIn {
//           from { opacity: 0; }
//           to { opacity: 1; }
//         }

//         /* Make the popup responsive */
//         @media (max-width: 640px) {
//           .md\\:flex-row {
//             flex-direction: column;
//           }

//           .md\\:w-1/2 {
//             width: 100%;
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// export default ProductCatalogSection;
